import React, { useState, useEffect } from 'react';
import './App.css';
import Icon from './icons';
import { ThemeToggle } from './theme';

const API_URL = process.env.REACT_APP_API_URL || '/api';

// CCMTeamDelegates is Adobe's "Team Delegates" admin group, not a licence — it
// grants no applications. Hidden from every product picker; the server refuses
// to assign it regardless, so this is convenience rather than the real guard.
const isBlockedProduct = (name) =>
    String(name || '').toLowerCase().replace(/[\s._-]/g, '').includes('teamdelegate');
const assignableProducts = (products) =>
    (Array.isArray(products) ? products : []).filter(p => !isBlockedProduct(p));

const ACTIVATION_MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

function getProductAvailability(org, product) {
    return org?.licenseAvailability?.[product] || null;
}

function isProductFull(org, product) {
    const availability = getProductAvailability(org, product);
    return availability?.limited === true && (availability.available || 0) <= 0;
}

function getAssignableProducts(org) {
    return (org?.products || []).filter(product => !isProductFull(org, product));
}

function formatProductAvailability(org, product) {
    const availability = getProductAvailability(org, product);
    if (!availability?.limited) return '';
    return availability.full ? ' - Full' : ` - ${availability.available} left`;
}

function ResellerDashboard({ reseller, token, onLogout }) {
    const [email, setEmail] = useState('');
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [bulkUserLimit, setBulkUserLimit] = useState(0);
    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [activeTab, setActiveTab] = useState(() => {
        const hash = window.location.hash.slice(1);
        const valid = ['dashboard','assign','bulkImport','users','bulkDelete','bulkExtend','history','feedback','branding'];
        return valid.includes(hash) ? hash : 'dashboard';
    });

    // Deletion history — shows auto-removed users from daily cleanup
    const [activityLogs, setActivityLogs] = useState([]);
    const [loadingActivity, setLoadingActivity] = useState(false);

    // Feedback
    const [myFeedback, setMyFeedback] = useState([]);
    const [loadingFeedback, setLoadingFeedback] = useState(false);
    const [feedbackType, setFeedbackType] = useState('suggestion');
    const [feedbackMessage, setFeedbackMessage] = useState('');
    const [submittingFeedback, setSubmittingFeedback] = useState(false);
    const [unseenFeedbackCount, setUnseenFeedbackCount] = useState(0);

    // Status page branding
    const [branding, setBranding] = useState({});
    const [brandingForm, setBrandingForm] = useState({ title: '', subtitle: '', brandColor: '' });
    const [loadingBranding, setLoadingBranding] = useState(false);
    const [savingBranding, setSavingBranding] = useState(false);
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [uploadingOrgIcon, setUploadingOrgIcon] = useState(false);
    const [stats, setStats] = useState({
        usedLicenses: 0,
        maxLicenses: 0,
        availableLicenses: 0,
        workingDays: 0,
        createdAt: null,
        allowTransfer: true,
        allowDeleteUsers: false,
        transferSourceOrgAction: 'remove',
        pausedActivationMonths: reseller?.pausedActivationMonths || []
    });

    // Organization & Product selection state
    const [organizationAccess, setOrganizationAccess] = useState([]);
    const [selectedOrg, setSelectedOrg] = useState(null);
    const [selectedProducts, setSelectedProducts] = useState([]);

    // Edit User State
    const [editingUser, setEditingUser] = useState(null);
    const [editFormProducts, setEditFormProducts] = useState([]);
    const [editEmail, setEditEmail] = useState('');
    const [editExtendMonths, setEditExtendMonths] = useState(0); // 0 = no extension
    // Destination org for a replacement — only used when the email actually changes
    const [editTargetOrg, setEditTargetOrg] = useState('');

    // Duration state for new assignments
    const [durationMonths, setDurationMonths] = useState(1);

    // Bulk organization change state
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [targetOrgForBulkChange, setTargetOrgForBulkChange] = useState(null);
    const [bulkChangeLoading, setBulkChangeLoading] = useState(false);

    // Bulk import state
    const [bulkCsvContent, setBulkCsvContent] = useState('');
    const [bulkDurationMonths, setBulkDurationMonths] = useState(1);
    const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0, startTime: null }); // Progress tracking

    // Bulk delete state
    const [bulkDeleteEmails, setBulkDeleteEmails] = useState('');

    // Bulk extend state
    const [bulkExtendEmails, setBulkExtendEmails] = useState('');
    const [bulkExtendMonths, setBulkExtendMonths] = useState(1);

    // Advanced filter state for My Users
    const [durationFilter, setDurationFilter] = useState('');
    const [daysFilter, setDaysFilter] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const USERS_PER_PAGE = 50;

    // Organization Filter
    const [selectedOrgFilter, setSelectedOrgFilter] = useState('All');

    // Calculate all unique organizations from users + organizationAccess
    const allUniqueOrgs = React.useMemo(() => {
        const orgs = [...organizationAccess];
        const orgMap = new Map();

        // Add active orgs
        orgs.forEach(o => orgMap.set(String(o.organizationId), o.organizationName));

        // Scan users for other orgs
        users.forEach(u => {
            if (u.organizationId && !orgMap.has(String(u.organizationId))) {
                orgMap.set(String(u.organizationId), u.organizationName || `Org ${u.organizationId}`);
            }
        });

        // Convert back to array
        return Array.from(orgMap.entries()).map(([id, name]) => ({
            organizationId: id,
            organizationName: name
        })).sort((a, b) => a.organizationName.localeCompare(b.organizationName));
    }, [users, organizationAccess]);

    // Bulk duration modification
    const [showBulkDurationModal, setShowBulkDurationModal] = useState(false);
    const [bulkNewDuration, setBulkNewDuration] = useState(1);

    // Bulk (Organizer) Transfer State
    const [showBulkTransferModal, setShowBulkTransferModal] = useState(false);
    const [targetOrgForTransfer, setTargetOrgForTransfer] = useState(null);
    const [targetProductsForTransfer, setTargetProductsForTransfer] = useState([]);

    // Derived state for bulk transfer products
    const bulkTransferDerivedProducts = React.useMemo(() => {
        if (!targetOrgForTransfer) return [];
        const orgInfo = organizationAccess.find(o => String(o.organizationId) === String(targetOrgForTransfer));
        return orgInfo ? orgInfo.products : [];
    }, [targetOrgForTransfer, organizationAccess]);

    // Migration status (for dashboard notification)
    const [migrationStatus, setMigrationStatus] = useState(null);

    // Announcements state
    const [announcements, setAnnouncements] = useState([]);
    const [showAnnouncementPopup, setShowAnnouncementPopup] = useState(false);
    const [dismissedAnnouncements, setDismissedAnnouncements] = useState(() => {
        const saved = localStorage.getItem('dismissedAnnouncements');
        return saved ? JSON.parse(saved) : [];
    });

    const isAnnouncementVisible = (id) => {
        return !dismissedAnnouncements.includes(id);
    };

    const availableActivationMonths = React.useMemo(
        () => {
            const pausedMonths = stats.pausedActivationMonths || reseller?.pausedActivationMonths || [];
            return ACTIVATION_MONTHS.filter(m => !pausedMonths.includes(m));
        },
        [stats.pausedActivationMonths, reseller?.pausedActivationMonths]
    );

    useEffect(() => {
        window.history.replaceState(null, '', window.location.pathname + '#' + activeTab);
    }, [activeTab]);

    useEffect(() => {
        if (activeTab === 'bulkDelete' && stats.allowDeleteUsers !== true) {
            setActiveTab('dashboard');
        }
    }, [activeTab, stats.allowDeleteUsers]);

    useEffect(() => {
        if (availableActivationMonths.length === 0) return;
        const firstAvailable = availableActivationMonths[0];
        if (!availableActivationMonths.includes(parseInt(durationMonths))) setDurationMonths(firstAvailable);
        if (!availableActivationMonths.includes(parseInt(bulkDurationMonths))) setBulkDurationMonths(firstAvailable);
        if (!availableActivationMonths.includes(parseInt(bulkExtendMonths))) setBulkExtendMonths(firstAvailable);
        if (!availableActivationMonths.includes(parseInt(bulkNewDuration))) setBulkNewDuration(firstAvailable);
        if (editExtendMonths > 0 && !availableActivationMonths.includes(parseInt(editExtendMonths))) setEditExtendMonths(0);
    }, [availableActivationMonths, durationMonths, bulkDurationMonths, bulkExtendMonths, bulkNewDuration, editExtendMonths]);

    const dismissAnnouncement = (id) => {
        const newDismissed = [...dismissedAnnouncements, id];
        setDismissedAnnouncements(newDismissed);
        localStorage.setItem('dismissedAnnouncements', JSON.stringify(newDismissed));
    };

    const fetchAnnouncements = async () => {
        try {
            const res = await fetch(`${API_URL}/reseller/announcements`, { headers: authHeaders });
            if (res.ok) {
                const data = await res.json();
                const activeAnnouncements = data.announcements || [];
                setAnnouncements(activeAnnouncements);
                // Show popup if there are any undismissed announcements
                const hasUndismissed = activeAnnouncements.some(a => !dismissedAnnouncements.includes(a.id));
                if (hasUndismissed) {
                    setShowAnnouncementPopup(true);
                }
            } else {
                console.error('Fetch failed:', res.status, res.statusText);
                const text = await res.text();
                console.error('Response:', text);
                // Helper for debugging:
                // alert(`Debug: Fetch failed. Status: ${res.status}. Check console.`);
            }
        } catch (err) {
            console.error('Failed to fetch announcements:', err);
        }
    };

    const authHeaders = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
    };

    const keepSourceOrgOnTransfer = stats.transferSourceOrgAction === 'keep';
    const transferBehaviorText = keepSourceOrgOnTransfer
        ? 'They will be added to the new organization and keep their current organization.'
        : 'They will be removed from their current organization and added to the new one.';

    // Check for active migrations (poll every 10 seconds)
    const fetchMigrationStatus = async () => {
        try {
            const res = await fetch(`${API_URL}/reseller/migration-status`, { headers: authHeaders });
            const data = await res.json();
            setMigrationStatus(data.inMigration ? data : null);
        } catch (err) {
            console.error('Failed to fetch migration status:', err);
        }
    };

    const fetchStats = async () => {
        try {
            const res = await fetch(`${API_URL}/reseller/stats`, {
                headers: authHeaders
            });
            const data = await res.json();
            if (data.success) {
                setStats(data.stats);
            }
        } catch (err) {
            console.error('Failed to fetch stats:', err);
        }
    };

    const fetchProducts = async () => {
        try {
            const res = await fetch(`${API_URL}/reseller/products`, {
                headers: authHeaders
            });
            const data = await res.json();
            if (data.success && data.organizationAccess) {
                setOrganizationAccess(data.organizationAccess);
                // Select first org by default
                if (data.organizationAccess.length > 0) {
                    const firstOrg = data.organizationAccess[0];
                    setSelectedOrg(firstOrg);
                    setSelectedProducts(getAssignableProducts(firstOrg));
                }
            }
        } catch (err) {
            console.error('Failed to fetch products:', err);
        }
    };

    const fetchUsers = async () => {
        setLoadingUsers(true);
        setError(null);
        try {
            const res = await fetch(`${API_URL}/reseller/users`, {
                headers: authHeaders
            });
            const data = await res.json();
            if (data.error) {
                setError(data.error);
            } else {
                const usersList = data.users || [];
                setUsers(usersList);
                setFilteredUsers(usersList);
            }
        } catch (err) {
            setError('Failed to fetch users: ' + err.message);
        }
        setLoadingUsers(false);
    };

    const fetchActivity = async () => {
        setLoadingActivity(true);
        try {
            const res = await fetch(`${API_URL}/reseller/activity?limit=200`, { headers: authHeaders });
            const data = await res.json();
            if (data.success) setActivityLogs(data.logs || []);
        } catch (err) {
            console.error('Failed to fetch activity:', err.message);
        }
        setLoadingActivity(false);
    };

    const fetchMyFeedback = async () => {
        setLoadingFeedback(true);
        try {
            const res = await fetch(`${API_URL}/reseller/feedback`, { headers: authHeaders });
            const data = await res.json();
            if (data.success) {
                setMyFeedback(data.feedback || []);
                const hasUnseen = (data.feedback || []).some(fb => fb.adminNote && !fb.resellerSeenAt);
                if (hasUnseen) {
                    fetch(`${API_URL}/reseller/feedback/mark-seen`, { method: 'PATCH', headers: authHeaders })
                        .then(() => {
                            const seenAt = new Date().toISOString();
                            setMyFeedback(prev => prev.map(fb => (fb.adminNote && !fb.resellerSeenAt) ? { ...fb, resellerSeenAt: seenAt } : fb));
                        })
                        .catch(err => console.error('Failed to mark feedback as seen:', err.message));
                }
            }
        } catch (err) {
            console.error('Failed to fetch feedback:', err.message);
        }
        setLoadingFeedback(false);
    };

    // Read-only count for the nav badge — deliberately does NOT mark anything
    // seen, so the badge keeps showing until the reseller actually opens the
    // Feedback tab (which calls fetchMyFeedback and marks them seen there).
    const fetchFeedbackUnseenCount = async () => {
        try {
            const res = await fetch(`${API_URL}/reseller/feedback`, { headers: authHeaders });
            const data = await res.json();
            if (data.success) {
                setUnseenFeedbackCount((data.feedback || []).filter(fb => fb.adminNote && !fb.resellerSeenAt).length);
            }
        } catch (err) {
            console.error('Failed to fetch feedback unseen count:', err.message);
        }
    };

    const handleSubmitFeedback = async (e) => {
        e.preventDefault();
        if (!feedbackMessage.trim()) return;
        setSubmittingFeedback(true);
        setError(null);
        try {
            const res = await fetch(`${API_URL}/reseller/feedback`, {
                method: 'POST',
                headers: authHeaders,
                body: JSON.stringify({ type: feedbackType, message: feedbackMessage.trim() })
            });
            const data = await res.json();
            if (res.ok && data.success) {
                setFeedbackMessage('');
                setResult(data.message || 'Feedback submitted. Thank you!');
                fetchMyFeedback();
            } else {
                setError(data.error || 'Failed to submit feedback');
            }
        } catch (err) {
            setError('Failed to submit feedback: ' + err.message);
        }
        setSubmittingFeedback(false);
    };

    const fetchBranding = async () => {
        setLoadingBranding(true);
        try {
            const res = await fetch(`${API_URL}/reseller/branding`, { headers: authHeaders });
            const data = await res.json();
            if (data.success) {
                setBranding(data.branding || {});
                setBrandingForm({
                    title: data.branding?.title || '',
                    subtitle: data.branding?.subtitle || '',
                    brandColor: data.branding?.brandColor || ''
                });
            }
        } catch (err) {
            console.error('Failed to fetch branding:', err.message);
        }
        setLoadingBranding(false);
    };

    const handleSaveBranding = async (e) => {
        e.preventDefault();
        setSavingBranding(true);
        setError(null);
        try {
            const res = await fetch(`${API_URL}/reseller/branding`, {
                method: 'PUT',
                headers: authHeaders,
                body: JSON.stringify(brandingForm)
            });
            const data = await res.json();
            if (res.ok && data.success) {
                setBranding(data.branding || {});
                setResult('Branding updated');
            } else {
                setError(data.error || 'Failed to update branding');
            }
        } catch (err) {
            setError('Failed to update branding: ' + err.message);
        }
        setSavingBranding(false);
    };

    const handleBrandingImageUpload = async (field, file) => {
        const endpoint = field === 'logo' ? 'logo' : 'org-icon';
        const formField = field === 'logo' ? 'logo' : 'orgIcon';
        const setUploading = field === 'logo' ? setUploadingLogo : setUploadingOrgIcon;

        setUploading(true);
        setError(null);
        try {
            const formData = new FormData();
            formData.append(formField, file);
            const res = await fetch(`${API_URL}/reseller/branding/${endpoint}`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });
            const data = await res.json();
            if (res.ok && data.success) {
                setBranding(data.branding || {});
            } else {
                setError(data.error || 'Failed to upload image');
            }
        } catch (err) {
            setError('Failed to upload image: ' + err.message);
        }
        setUploading(false);
    };

    const handleRemoveBrandingImage = async (field) => {
        const endpoint = field === 'logo' ? 'logo' : 'org-icon';
        try {
            const res = await fetch(`${API_URL}/reseller/branding/${endpoint}`, {
                method: 'DELETE',
                headers: authHeaders
            });
            const data = await res.json();
            if (res.ok && data.success) setBranding(data.branding || {});
        } catch (err) {
            console.error('Failed to remove image:', err.message);
        }
    };

    useEffect(() => {
        fetchStats();
        fetchProducts();
        fetchMigrationStatus();
        fetchAnnouncements();
        fetchFeedbackUnseenCount();
        fetch(`${API_URL}/bulk-limit`).then(r => r.json()).then(d => {
            if (d.maxUsersPerOp > 0) setBulkUserLimit(d.maxUsersPerOp);
        }).catch(() => {});

        // Poll migration status every 10 seconds
        const migrationInterval = setInterval(fetchMigrationStatus, 10000);

        return () => clearInterval(migrationInterval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Advanced filtering effect for My Users
    useEffect(() => {
        let filtered = users;

        // Text search filter
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(user =>
                user.email.toLowerCase().includes(query) ||
                (user.firstname && user.firstname.toLowerCase().includes(query)) ||
                (user.lastname && user.lastname.toLowerCase().includes(query))
            );
        }

        // Duration filter
        if (durationFilter) {
            const duration = parseInt(durationFilter);
            filtered = filtered.filter(user => (user.durationMonths || 1) === duration);
        }

        // Days remaining filter
        if (daysFilter !== '') {
            const days = parseInt(daysFilter);
            if (!isNaN(days)) {
                filtered = filtered.filter(user => {
                    if (user.daysLeft === null || user.daysLeft === undefined) return false;
                    return user.daysLeft <= days;
                });
            }
        }



        // Organization Filter
        if (selectedOrgFilter !== 'All') {
            if (selectedOrgFilter === 'Unknown') {
                filtered = filtered.filter(u => !u.organizationId);
            } else {
                // Check if organizationId matches (if present) or if organizationName matches
                filtered = filtered.filter(u => {
                    if (u.organizationId) {
                        return String(u.organizationId) === String(selectedOrgFilter);
                    } else if (u.organizationName) {
                        const orgName = allUniqueOrgs.find(o => String(o.organizationId) === String(selectedOrgFilter))?.organizationName;
                        return orgName && u.organizationName === orgName;
                    }
                    return false;
                });
            }
        }

        setFilteredUsers(filtered);
        setCurrentPage(1); // Reset to page 1 when filters change
    }, [users, searchQuery, durationFilter, daysFilter, selectedOrgFilter]);

    // Refetch products when switching to Add User or Bulk Import tab
    // Ensures traditional resellers see updated product list after admin changes org access (e.g. removes a license)
    useEffect(() => {
        if (activeTab === 'assign' || activeTab === 'bulk') {
            fetchProducts();
        }
    }, [activeTab]);

    // Pagination calculations
    const totalPages = Math.ceil(filteredUsers.length / USERS_PER_PAGE);
    const paginatedUsers = filteredUsers.slice(
        (currentPage - 1) * USERS_PER_PAGE,
        currentPage * USERS_PER_PAGE
    );

    const handleAssignUser = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setResult(null);

        try {
            const res = await fetch(`${API_URL}/reseller/assign-user`, {
                method: 'POST',
                headers: authHeaders,
                body: JSON.stringify({
                    email,
                    selectedProducts: selectedProducts,
                    organizationId: selectedOrg?.organizationId,
                    durationMonths: durationMonths
                }),
            });
            const data = await res.json();

            if (!res.ok || data.error) {
                setError(data.message || data.error || 'Failed to assign user');
            } else if (data.success === false) {
                setError(data.message || 'Assignment completed with errors');
            } else {
                setResult(data.message || 'User assigned successfully!');
                setEmail('');
                fetchStats();
                if (activeTab === 'users') {
                    fetchUsers();
                }
            }
        } catch (err) {
            setError('Failed to connect to server: ' + err.message);
        }
        setLoading(false);
    };

    const handleEditClick = (user) => {
        setEditingUser(user);
        setEditEmail(user.email);
        setEditTargetOrg(String(user.organizationId ?? ''));

        // Map user.products to match organizationAccess products to avoid name mismatches
        const userOrgAccess = organizationAccess.find(o => o.organizationId === user.organizationId);
        const availableProducts = userOrgAccess?.products || [];
        const userProducts = Array.isArray(user.products) ? user.products : [];

        // Map each user product to the best matching product in organizationAccess
        const mappedProducts = userProducts.map(userProduct => {
            // 1. Exact match
            if (availableProducts.includes(userProduct)) {
                return userProduct;
            }

            // 2. Case-insensitive exact match
            const caseInsensitiveMatch = availableProducts.find(ap =>
                ap.toLowerCase() === userProduct.toLowerCase()
            );
            if (caseInsensitiveMatch) return caseInsensitiveMatch;

            // 3. Handle "Default All Apps - Edition X configuration" variants
            if (/Default All Apps.*Edition/i.test(userProduct)) {
                const allAppsMatch = availableProducts.find(ap =>
                    /All Apps.*Configuration|Default All Apps/i.test(ap)
                );
                if (allAppsMatch) return allAppsMatch;
            }

            // 4. Handle "Default All Apps" -> "Creative Cloud All Apps Configuration"
            if (/Default All Apps/i.test(userProduct) || userProduct === 'Creative Cloud All Apps') {
                const allAppsConfig = availableProducts.find(ap =>
                    /All Apps.*Configuration|Creative Cloud All Apps Configuration/i.test(ap)
                );
                if (allAppsConfig) return allAppsConfig;
            }

            // 5. Handle "Creative Cloud All Apps" -> "Creative Cloud All Apps Configuration"
            if (/Creative Cloud All Apps$/i.test(userProduct)) {
                const configMatch = availableProducts.find(ap =>
                    ap === userProduct + ' Configuration' ||
                    /Creative Cloud All Apps Configuration/i.test(ap)
                );
                if (configMatch) return configMatch;
            }

            // 6. Handle Pro Plus variants
            if (/Pro Plus/i.test(userProduct)) {
                const proPlusMatch = availableProducts.find(ap =>
                    /Pro Plus/i.test(ap)
                );
                if (proPlusMatch) return proPlusMatch;
            }

            // 7. Fuzzy match: if product name contains key terms, try to find similar
            const userProductLower = userProduct.toLowerCase();
            if (userProductLower.includes('all apps')) {
                const fuzzyMatch = availableProducts.find(ap =>
                    ap.toLowerCase().includes('all apps')
                );
                if (fuzzyMatch) return fuzzyMatch;
            }

            // 8. If only one product available and user has products, use that
            if (availableProducts.length === 1 && userProducts.length > 0) {
                return availableProducts[0];
            }

            // Return original if no match found (will be validated by backend)
            return userProduct;
        }).filter(p => p); // Remove any null/undefined

        setEditFormProducts(mappedProducts);
        setEditExtendMonths(0); // Reset extend months
        setError(null);
        setResult(null);
    };

    const handleUpdateUser = async (e) => {
        e.preventDefault();
        if (!editingUser) return;

        setLoading(true);
        setError(null);

        try {
            // Check if email is being changed (user replacement)
            const isEmailChange = editEmail && editEmail !== editingUser.email;
            // Only a replacement may also move the license to another organization
            const isOrgChange = !!(isEmailChange && editTargetOrg
                && String(editTargetOrg) !== String(editingUser.organizationId));

            const res = await fetch(`${API_URL}/reseller/update-user`, {
                method: 'POST',
                headers: authHeaders,
                body: JSON.stringify({
                    email: editingUser.email,
                    newEmail: isEmailChange ? editEmail : undefined, // Send new email for replacement
                    organizationId: editingUser.organizationId,
                    newOrganizationId: isOrgChange ? editTargetOrg : undefined, // Destination org for the replacement
                    selectedProducts: editFormProducts,
                    extendMonths: editExtendMonths > 0 ? editExtendMonths : undefined
                }),
            });
            const data = await res.json();

            if (!res.ok || data.error) {
                alert(data.message || data.error || 'Failed to update user');
            } else {
                // Success
                setEditingUser(null);
                setResult(data.message || 'User updated successfully');
                fetchUsers(); // Refresh list
                fetchStats(); // Refresh stats in case of user replacement
            }
        } catch (err) {
            alert('Failed to update: ' + err.message);
        }
        setLoading(false);
    };

    const handleRemoveUser = async (userEmail) => {
        if (stats.allowDeleteUsers !== true) {
            setError('User deletion is not enabled for this account');
            return;
        }
        if (!window.confirm(`Are you sure you want to remove ${userEmail} ? `)) return;

        setError(null);
        setResult(null);
        try {
            const res = await fetch(`${API_URL}/reseller/remove-user`, {
                method: 'POST',
                headers: authHeaders,
                body: JSON.stringify({ email: userEmail }),
            });
            const data = await res.json();
            if (data.error) {
                setError(data.error);
            } else {
                setResult(`User ${userEmail} removed successfully!`);
                fetchUsers();
                fetchStats();
            }
        } catch (err) {
            setError('Failed to remove user: ' + err.message);
        }
    };

    const handleCopyStatusLink = async (userEmail) => {
        const statusUrl = `${window.location.origin}/status/${encodeURIComponent(userEmail)}`;

        try {
            await navigator.clipboard.writeText(statusUrl);
            setResult(`Copied status link for ${userEmail}`);
        } catch (err) {
            setError('Failed to copy status link: ' + err.message);
        }
    };

    // Toggle user selection for bulk operations
    const toggleUserSelection = (userEmail) => {
        setSelectedUsers(prev =>
            prev.includes(userEmail)
                ? prev.filter(e => e !== userEmail)
                : [...prev, userEmail]
        );
    };

    // Select/deselect all users
    const toggleSelectAll = () => {
        if (selectedUsers.length === filteredUsers.length) {
            setSelectedUsers([]);
        } else {
            setSelectedUsers(filteredUsers.map(u => u.email));
        }
    };

    // Handle bulk organization change
    const handleBulkOrgChange = async () => {
        if (selectedUsers.length === 0) {
            setError('Please select at least one user');
            return;
        }
        if (!targetOrgForBulkChange) {
            setError('Please select target organization');
            return;
        }
        if (!window.confirm(`Move ${selectedUsers.length} user(s) to ${targetOrgForBulkChange.organizationName}?\n\n${transferBehaviorText}`)) return;

        setBulkChangeLoading(true);
        setError(null);
        setResult(null);

        let successCount = 0;
        let failCount = 0;

        for (const userEmail of selectedUsers) {
            try {
                const res = await fetch(`${API_URL}/reseller/change-user-org`, {
                    method: 'POST',
                    headers: authHeaders,
                    body: JSON.stringify({
                        email: userEmail,
                        newOrganizationId: targetOrgForBulkChange.organizationId,
                        products: targetOrgForBulkChange.products || []
                    }),
                });
                const data = await res.json();
                if (data.success) {
                    successCount++;
                } else {
                    failCount++;
                }
            } catch (err) {
                failCount++;
            }
        }

        setBulkChangeLoading(false);
        setSelectedUsers([]);
        fetchUsers();
        fetchStats();

        if (failCount === 0) {
            setResult(
                keepSourceOrgOnTransfer
                    ? `Successfully added ${successCount} user(s) to ${targetOrgForBulkChange.organizationName} while keeping their current organization.`
                    : `Successfully moved ${successCount} user(s) to ${targetOrgForBulkChange.organizationName}`
            );
        } else {
            setResult(
                keepSourceOrgOnTransfer
                    ? `Added ${successCount} user(s) to ${targetOrgForBulkChange.organizationName}. ${failCount} failed.`
                    : `Moved ${successCount} user(s). ${failCount} failed.`
            );
        }
    };

    // Handle bulk import of users from CSV - chunked batching with live progress
    const handleBulkImport = async (e) => {
        e.preventDefault();
        if (!selectedOrg) {
            setError('Please select an organization first');
            return;
        }
        if (selectedProducts.length === 0) {
            setError('Please select at least one product');
            return;
        }

        setLoading(true);
        setError(null);
        setResult(null);

        // Parse emails from CSV content
        const emails = bulkCsvContent
            .split(/[,\n\r]+/)
            .map(e => e.trim())
            .filter(e => e && e.includes('@'));

        if (emails.length === 0) {
            setError('No valid email addresses found');
            setLoading(false);
            return;
        }

        // Check if we have enough licenses
        if (emails.length > stats.availableLicenses) {
            setError(`Not enough licenses. You have ${stats.availableLicenses} available but trying to add ${emails.length} users.`);
            setLoading(false);
            return;
        }

        // Chunked batching - send 50 emails per request (smaller chunks for reliability)
        const CHUNK_SIZE = 50;
        const startTime = Date.now();
        let totalSuccess = 0;
        let totalFailed = 0;
        let totalSkipped = 0;
        const allFailures = [];

        setBulkProgress({ current: 0, total: emails.length, startTime });

        for (let i = 0; i < emails.length; i += CHUNK_SIZE) {
            const chunk = emails.slice(i, i + CHUNK_SIZE);
            const processedSoFar = i; // Users already processed before this chunk

            try {
                // Add timeout for large requests (2 minutes per chunk)
                const controller = new AbortController();
                const timeoutId = setTimeout(() => controller.abort(), 120000); // 2 minute timeout

                const res = await fetch(`${API_URL}/reseller/bulk-assign-users`, {
                    method: 'POST',
                    headers: authHeaders,
                    signal: controller.signal,
                    body: JSON.stringify({
                        emails: chunk,
                        selectedProducts: selectedProducts,
                        organizationId: selectedOrg?.organizationId,
                        durationMonths: bulkDurationMonths
                    }),
                });

                clearTimeout(timeoutId);

                if (!res.ok) {
                    // Try to parse response anyway - server might have partially succeeded or have a message
                    let serverMsg = 'Server error';
                    try {
                        const data = await res.json();
                        if (data.results) {
                            totalSuccess += data.results.success || 0;
                            totalFailed += data.results.failed || 0;
                            totalSkipped += data.results.skipped || 0;
                            if (data.results.failures) {
                                allFailures.push(...data.results.failures);
                            }
                            continue;
                        }
                        if (data.error) serverMsg = data.error;
                    } catch (parseErr) {
                        // Can't parse, keep generic message
                    }
                    console.error('Chunk failed with status:', res.status, serverMsg);
                    totalFailed += chunk.length;
                    chunk.forEach(email => allFailures.push({ email, error: serverMsg }));
                    continue;
                }

                const data = await res.json();

                if (data.success && data.results) {
                    totalSuccess += data.results.success || 0;
                    totalFailed += data.results.failed || 0;
                    totalSkipped += data.results.skipped || 0;
                    if (data.results.failures) {
                        allFailures.push(...data.results.failures);
                    }
                } else if (data.results) {
                    // Partial success even without success flag
                    totalSuccess += data.results.success || 0;
                    totalFailed += data.results.failed || 0;
                    totalSkipped += data.results.skipped || 0;
                } else {
                    // Count chunk as failed
                    totalFailed += chunk.length;
                    chunk.forEach(email => allFailures.push({ email, error: data.error || 'Unknown error' }));
                }
            } catch (err) {
                console.error('Chunk error:', err);
                // Check if it's a timeout/abort error
                if (err.name === 'AbortError') {
                    // Timeout - server might still be processing, don't count as total failure
                    console.warn('Request timed out, server may still be processing');
                    chunk.forEach(email => allFailures.push({ email, error: 'Request timeout (may succeed)' }));
                } else {
                    chunk.forEach(email => allFailures.push({ email, error: 'Connection error' }));
                }
                totalFailed += chunk.length;
            }

            // Update progress after chunk completes
            const processedNow = Math.min(i + CHUNK_SIZE, emails.length);
            setBulkProgress({ current: processedNow, total: emails.length, startTime });

            // Small delay between chunks to prevent overload
            if (i + CHUNK_SIZE < emails.length) {
                await new Promise(r => setTimeout(r, 200));
            }
        }

        // Final progress update
        setBulkProgress({ current: emails.length, total: emails.length, startTime });

        setLoading(false);
        setBulkProgress({ current: 0, total: 0, startTime: null });
        fetchStats();

        // Show results
        setResult(`Assigned ${totalSuccess} users. ${totalFailed} failed. ${totalSkipped} skipped (already assigned).`);
        if (totalFailed === 0 && totalSkipped === emails.length - totalSuccess) {
            setBulkCsvContent('');
        }
        if (allFailures.length > 0) {
            setError('Some failures:\n' + allFailures.slice(0, 10).map(f => `${f.email}: ${f.error}`).join('\n') +
                (allFailures.length > 10 ? `\n...and ${allFailures.length - 10} more` : ''));
        }
    };

    // Handle bulk delete of multiple users by email - optimized with chunked batching
    const handleBulkDelete = async (e) => {
        e.preventDefault();
        if (stats.allowDeleteUsers !== true) {
            setError('User deletion is not enabled for this account');
            return;
        }
        setLoading(true);
        setError(null);
        setResult(null);

        // Parse emails from textarea (comma, newline, or semicolon separated)
        const rawEmails = bulkDeleteEmails
            .split(/[\n,;]+/)
            .map(email => email.trim().toLowerCase())
            .filter(email => email && email.includes('@'));

        if (rawEmails.length === 0) {
            setError('No valid email addresses found. Please enter at least one email address.');
            setLoading(false);
            return;
        }

        // Confirm deletion
        if (!window.confirm(`Are you sure you want to delete ${rawEmails.length} user(s)? This action cannot be undone.`)) {
            setLoading(false);
            return;
        }

        let totalSuccess = 0;
        let totalFailed = 0;
        let totalNotFound = 0;
        const allFailures = [];

        // Chunked batching - send 50 emails per request
        const CHUNK_SIZE = 50;

        for (let i = 0; i < rawEmails.length; i += CHUNK_SIZE) {
            const chunk = rawEmails.slice(i, i + CHUNK_SIZE);
            setResult(`Deleting ${Math.min(i + CHUNK_SIZE, rawEmails.length)}/${rawEmails.length} users...`);

            try {
                const res = await fetch(`${API_URL}/reseller/bulk-delete-users`, {
                    method: 'POST',
                    headers: authHeaders,
                    body: JSON.stringify({ emails: chunk }),
                });

                if (!res.ok) {
                    let serverMsg = 'Server error';
                    try { const errData = await res.json(); if (errData.error) serverMsg = errData.error; } catch {}
                    totalFailed += chunk.length;
                    chunk.forEach(email => allFailures.push({ email, error: serverMsg }));
                    continue;
                }

                const data = await res.json();

                if (data.success && data.results) {
                    totalSuccess += data.results.success || 0;
                    totalFailed += data.results.failed || 0;
                    totalNotFound += data.results.notFound || 0;
                    if (data.results.failures) {
                        allFailures.push(...data.results.failures);
                    }
                } else {
                    totalFailed += chunk.length;
                }
            } catch (err) {
                totalFailed += chunk.length;
                chunk.forEach(email => allFailures.push({ email, error: 'Connection error' }));
            }

            // Small delay between chunks
            if (i + CHUNK_SIZE < rawEmails.length) {
                await new Promise(r => setTimeout(r, 200));
            }
        }

        setLoading(false);

        // Build result message
        let resultMessage = `Deleted ${totalSuccess} user(s).`;
        if (totalNotFound > 0) {
            resultMessage += ` ${totalNotFound} not found.`;
        }
        if (totalFailed > 0) {
            resultMessage += ` ${totalFailed} failed.`;
        }

        setResult(resultMessage);
        if (totalFailed === 0) {
            setBulkDeleteEmails('');
        }
        fetchUsers();
        fetchStats();

        if (allFailures.length > 0) {
            setError('Some failures:\n' + allFailures.slice(0, 10).map(f => `${f.email}: ${f.error}`).join('\n') +
                (allFailures.length > 10 ? `\n...and ${allFailures.length - 10} more` : ''));
        }
    };

    // Handle bulk extend of multiple users by email
    const handleBulkExtend = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setResult(null);

        const rawEmails = bulkExtendEmails
            .split(/[\n,;]+/)
            .map(email => email.trim().toLowerCase())
            .filter(email => email && email.includes('@'));

        if (rawEmails.length === 0) {
            setError('No valid email addresses found.');
            setLoading(false);
            return;
        }

        if (!window.confirm(`Extend ${rawEmails.length} user(s) by ${bulkExtendMonths} month(s)?`)) {
            setLoading(false);
            return;
        }

        try {
            const res = await fetch(`${API_URL}/reseller/bulk-extend-users`, {
                method: 'POST',
                headers: authHeaders,
                body: JSON.stringify({ emails: rawEmails, extensionMonths: bulkExtendMonths }),
            });

            const data = await res.json();
            setLoading(false);

            if (res.ok && data.success) {
                setResult(`Extended ${data.results?.extended || 0} user(s) by ${bulkExtendMonths} month(s). ${data.results?.notFound || 0} not found.`);
                setBulkExtendEmails('');
                fetchUsers();
                fetchStats();
            } else {
                setError(data.error || 'Bulk extend failed');
            }
        } catch (err) {
            setLoading(false);
            setError(`Bulk extend failed: ${err.message}`);
        }
    };

    // Handle bulk duration modification
    const handleBulkModifyDuration = async () => {
        if (selectedUsers.length === 0) {
            setError('No users selected');
            return;
        }

        setLoading(true);
        setError(null);
        setResult(null);

        try {
            const res = await fetch(`${API_URL}/reseller/bulk-modify-duration`, {
                method: 'POST',
                headers: authHeaders,
                body: JSON.stringify({
                    userEmails: selectedUsers,
                    newDurationMonths: bulkNewDuration
                }),
            });
            const data = await res.json();
            setLoading(false);

            if (res.ok && data.success) {
                setResult(data.message);

                if (data.skipped > 0) {
                    setError(`Warning: ${data.skipped} users skipped (see console for details).`);
                    console.log('Skipped users:', data.skippedDetails);
                }

                setShowBulkDurationModal(false);
                setSelectedUsers([]);
                fetchUsers();
                fetchStats();
            } else {
                setError(data.error || 'Failed to modify duration');
            }
        } catch (err) {
            setLoading(false);
            setError(`Failed to modify duration: ${err.message}`);
        }
    };

    // Handle Bulk Transfer
    const handleBulkTransfer = async () => {
        if (selectedUsers.length === 0) return;
        if (!targetOrgForTransfer) {
            setError('Please select a target organization');
            return;
        }
        if (targetProductsForTransfer.length === 0) {
            setError('Please select at least one product for the users in the new organization');
            return;
        }

        const orgName = organizationAccess.find(o => String(o.organizationId) === String(targetOrgForTransfer))?.organizationName || 'Target Org';

        if (!window.confirm(`Warning: You are about to move ${selectedUsers.length} users to "${orgName}".\n\n${transferBehaviorText}\n\nContinue?`)) {
            return;
        }

        setLoading(true);
        setError(null);
        setResult(null);

        try {
            // Use the new bulk endpoint
            const res = await fetch(`${API_URL}/reseller/bulk-transfer-org`, {
                method: 'POST',
                headers: authHeaders,
                body: JSON.stringify({
                    emails: selectedUsers,
                    newOrganizationId: targetOrgForTransfer,
                    products: targetProductsForTransfer
                }),
            });

            const data = await res.json();
            // setLoading(false); // Moved to specific paths

            if (res.ok && data.success) {
                // Check for Async Processing
                if (data.processing && data.operationId) {
                    setLoading(true); // Ensure loading stays true
                    // Poll for status
                    const pollId = setInterval(async () => {
                        try {
                            const statusRes = await fetch(`${API_URL}/reseller/bulk-transfer-status/${data.operationId}`, { headers: authHeaders });
                            const status = await statusRes.json();

                            if (status.status === 'completed' || status.status === 'failed') {
                                clearInterval(pollId);
                                setLoading(false);

                                if (status.failCount > 0) {
                                    setResult(null);
                                    setError(
                                        `${keepSourceOrgOnTransfer ? 'Added' : 'Transferred'} ${status.successCount} users, but ${status.failCount} failed.\n\n` +
                                        `Failures:\n${status.failures.join('\n')}`
                                    );
                                } else {
                                    setResult(
                                        keepSourceOrgOnTransfer
                                            ? `Successfully added ${status.successCount} users to ${orgName} while keeping their current organization.`
                                            : `Successfully transferred ${status.successCount} users.`
                                    );
                                }

                                setShowBulkTransferModal(false);
                                setSelectedUsers([]);
                                setTargetOrgForTransfer(null);
                                setTargetProductsForTransfer([]);
                                fetchUsers();
                                fetchStats();
                            }
                            // Else keep waiting

                        } catch (e) {
                            clearInterval(pollId);
                            setLoading(false);
                            setError('Error verifying transfer status. Please check "My Users" list later.');
                            fetchUsers();
                        }
                    }, 2000); // Check every 2 seconds

                    return; // Exit function, let interval handle the rest
                }

                // If there are failures (Legacy/Sync), show them
                setLoading(false); // Done for sync path
                if (data.failures && data.failures.length > 0) {
                    setResult(null); // Clear success message preference
                    setError(
                        `${keepSourceOrgOnTransfer ? 'Added' : 'Transferred'} ${data.successCount} users, but ${data.failureCount} failed.\n\n` +
                        `Failures:\n${data.failures.join('\n')}`
                    );
                } else {
                    setResult(data.message);
                }

                setShowBulkTransferModal(false);
                setSelectedUsers([]);
                setTargetOrgForTransfer(null);
                setTargetProductsForTransfer([]);
                fetchUsers();
                fetchStats();
            } else {
                setLoading(false);
                setError(data.error || data.message || 'Bulk transfer failed');
            }
        } catch (err) {
            setLoading(false);
            setError(`Bulk transfer failed: ${err.message}`);
        }
    };


    const handleLogout = () => {
        localStorage.removeItem('resellerToken');
        localStorage.removeItem('resellerInfo');
        onLogout();
    };

    // Export users to CSV
    const handleExportUsers = () => {
        if (users.length === 0) {
            setError('No users to export');
            return;
        }

        // Get organization names
        const orgMap = {};
        organizationAccess.forEach(org => {
            orgMap[org.organizationId] = org.name;
        });

        // Build CSV content
        const headers = ['Email', 'First Name', 'Last Name', 'Products', 'Duration (months)', 'Days Left', 'Organization', 'Status'];
        const rows = users.map(user => {
            const expiryDate = user.expiresAt ? new Date(user.expiresAt).toLocaleDateString() : 'N/A';
            const daysLeft = user.expiresAt ? Math.ceil((new Date(user.expiresAt) - new Date()) / (1000 * 60 * 60 * 24)) : 0;
            const daysLeftStr = user.expiresAt ? (daysLeft <= 0 ? 'Expired' : `${daysLeft} days`) : 'N/A';

            const products = Array.isArray(user.products) ? user.products.join('; ') : (user.products || 'N/A');
            const orgName = orgMap[user.organizationId] || user.organizationName || 'N/A'; // Also try user.organizationName as fallback
            const status = user.expiresAt && new Date(user.expiresAt) < new Date() ? 'Expired' : 'Active';
            return [
                user.email || '',
                user.firstname || 'User',
                user.lastname || 'Name',
                products,
                user.durationMonths || 1,
                daysLeftStr, // Changed from expiryDate
                orgName,
                status
            ].map(val => `"${String(val).replace(/"/g, '""')}"`).join(',');
        });

        const csv = [headers.join(','), ...rows].join('\n');

        // Download file
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `users_export_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        setResult(`Exported ${users.length} users to CSV`);
    };

    const getPageTitle = () => {
        switch (activeTab) {
            case 'dashboard': return 'Dashboard';
            case 'assign': return 'Add User';
            case 'bulk': return 'Bulk Import';
            case 'bulkDelete': return 'Bulk Delete';
            case 'bulkExtend': return 'Bulk Extend';
            case 'users': return 'My Users';
            case 'history': return 'History';
            default: return 'Dashboard';
        }
    };

    const licensePercentage = stats.maxLicenses > 0
        ? Math.round((stats.usedLicenses / stats.maxLicenses) * 100)
        : 0;

    return (
        <div className="App">
            {/* Announcement Popup Modal */}
            {showAnnouncementPopup && (
                <div className="modal-overlay" style={{ zIndex: 9999 }} onClick={() => setShowAnnouncementPopup(false)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '500px' }}>
                        <div className="modal-header">
                            <h3 className="modal-title">Announcements</h3>
                            <button className="modal-close" onClick={() => setShowAnnouncementPopup(false)}>×</button>
                        </div>
                        <div className="modal-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                            {announcements.length === 0 ? (
                                <div className="empty-state" style={{ padding: '20px' }}>
                                    <div style={{ fontSize: '40px', marginBottom: '10px' }}><Icon name="mail" size={16} /></div>
                                    <p>No active announcements found.</p>
                                    <button
                                        onClick={() => setShowAnnouncementPopup(false)}
                                        className="btn btn-secondary"
                                        style={{ marginTop: '10px' }}
                                    >
                                        Close
                                    </button>
                                </div>
                            ) : (
                                announcements.map(ann => {
                                    const isDismissed = !isAnnouncementVisible(ann.id);
                                    return (
                                        <div key={ann.id} style={{
                                            background: isDismissed ? '#f1f5f9' : (ann.type === 'alert' ? '#fee2e2' : '#f0f9ff'),
                                            border: `1px solid ${isDismissed ? '#e2e8f0' : (ann.type === 'alert' ? '#fecaca' : '#bae6fd')}`,
                                            borderRadius: '8px',
                                            padding: '16px',
                                            marginBottom: '12px',
                                            opacity: isDismissed ? 0.7 : 1
                                        }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', alignItems: 'flex-start' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <strong style={{ color: isDismissed ? '#64748b' : (ann.type === 'alert' ? '#b91c1c' : '#0369a1'), fontSize: '16px' }}>
                                                        {isDismissed ? '' : ''}{ann.title}
                                                    </strong>
                                                    {isDismissed && <span style={{ fontSize: '10px', background: '#e2e8f0', padding: '2px 4px', borderRadius: '4px' }}>SEEN</span>}
                                                </div>
                                                <span style={{ fontSize: '12px', color: '#64748b' }}>{new Date(ann.createdAt).toLocaleDateString()}</span>
                                            </div>
                                            <p style={{ margin: '0 0 12px 0', whiteSpace: 'pre-wrap', color: '#334155', lineHeight: '1.5' }}>
                                                {ann.message}
                                            </p>
                                            {!isDismissed && (
                                                <button
                                                    onClick={() => dismissAnnouncement(ann.id)}
                                                    style={{
                                                        background: '#fff',
                                                        border: '1px solid #cbd5e1',
                                                        padding: '6px 12px',
                                                        borderRadius: '4px',
                                                        fontSize: '13px',
                                                        cursor: 'pointer',
                                                        color: '#475569',
                                                        width: '100%'
                                                    }}
                                                >
                                                    Mark as Read
                                                </button>
                                            )}
                                        </div>
                                    );
                                })
                            )}
                        </div>
                        <div className="modal-footer" style={{ borderTop: '1px solid #e2e8f0', padding: '16px', marginTop: '0', display: 'flex', justifyContent: 'flex-end' }}>
                            <button className="btn btn-primary" onClick={() => setShowAnnouncementPopup(false)}>Close</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Sidebar */}
            <div className="sidebar reseller-sidebar">
                <div className="sidebar-header">
                    <h1>Reseller Panel</h1>
                    <p>Group: {reseller.userGroup}</p>
                </div>
                {announcements.filter(a => isAnnouncementVisible(a.id)).length > 0 && (
                    <div style={{ margin: '0 12px 12px 12px' }}>
                        {announcements.filter(a => isAnnouncementVisible(a.id)).map(ann => (
                            <div key={ann.id} style={{
                                background: '#3b82f6',
                                borderRadius: '8px',
                                padding: '12px',
                                marginBottom: '8px',
                                color: 'white',
                                position: 'relative',
                                boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                            }}>
                                <div style={{ fontWeight: 'bold', marginBottom: '4px', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <span><Icon name="megaphone" size={16} /></span> {ann.title}
                                </div>
                                <div style={{ fontSize: '12px', opacity: 0.9, marginBottom: '8px', whiteSpace: 'pre-wrap' }}>
                                    {ann.message}
                                </div>
                                <button
                                    onClick={(e) => { e.stopPropagation(); dismissAnnouncement(ann.id); }}
                                    style={{
                                        width: '100%',
                                        background: 'rgba(255,255,255,0.2)',
                                        border: 'none',
                                        borderRadius: '4px',
                                        color: 'white',
                                        cursor: 'pointer',
                                        padding: '4px',
                                        fontSize: '11px'
                                    }}
                                >
                                    Dismiss
                                </button>
                            </div>
                        ))}
                    </div>
                )}
                <nav className="nav-menu">
                    <div
                        className={`nav-item ${activeTab === 'dashboard' ? 'active' : ''}`}
                        onClick={() => { setActiveTab('dashboard'); fetchStats(); setError(null); setResult(null); }}
                    >
                        <span className="nav-icon"><Icon name="chart" /></span>
                        <span>Dashboard</span>
                    </div>
                    <div
                        className={`nav-item ${activeTab === 'assign' ? 'active' : ''}`}
                        onClick={() => { setActiveTab('assign'); setError(null); setResult(null); }}
                    >
                        <span className="nav-icon"><Icon name="plus" /></span>
                        <span>Add User</span>
                    </div>
                    <div
                        className={`nav-item ${activeTab === 'bulk' ? 'active' : ''}`}
                        onClick={() => { setActiveTab('bulk'); setError(null); setResult(null); }}
                    >
                        <span className="nav-icon"><Icon name="file" /></span>
                        <span>Bulk Import</span>
                    </div>
                    {stats.allowDeleteUsers === true && (
                    <div
                        className={`nav-item ${activeTab === 'bulkDelete' ? 'active' : ''}`}
                        onClick={() => { setActiveTab('bulkDelete'); setError(null); setResult(null); }}
                    >
                        <span className="nav-icon"><Icon name="trash" /></span>
                        <span>Bulk Delete</span>
                    </div>
                    )}
                    <div
                        className={`nav-item ${activeTab === 'bulkExtend' ? 'active' : ''}`}
                        onClick={() => { setActiveTab('bulkExtend'); setError(null); setResult(null); }}
                    >
                        <span className="nav-icon"><Icon name="clock" /></span>
                        <span>Bulk Extend</span>
                    </div>

                    <div className="nav-item" onClick={() => setShowAnnouncementPopup(true)}>
                        <span className="nav-icon"><Icon name="megaphone" /></span>
                        <span>Announcements</span>
                    </div>
                    <div
                        className={`nav-item ${activeTab === 'users' ? 'active' : ''}`}
                        onClick={() => { setActiveTab('users'); fetchUsers(); setError(null); setResult(null); }}
                    >
                        <span className="nav-icon"><Icon name="users" /></span>
                        <span>My Users</span>
                    </div>
                    <div
                        className={`nav-item ${activeTab === 'history' ? 'active' : ''}`}
                        onClick={() => { setActiveTab('history'); fetchActivity(); setError(null); setResult(null); }}
                    >
                        <span className="nav-icon"><Icon name="clipboard" /></span>
                        <span>History</span>
                    </div>
                    <div
                        className={`nav-item ${activeTab === 'feedback' ? 'active' : ''}`}
                        onClick={() => { setActiveTab('feedback'); fetchMyFeedback(); setUnseenFeedbackCount(0); setError(null); setResult(null); }}
                    >
                        <span className="nav-icon" style={{ position: 'relative' }}>
                            {unseenFeedbackCount > 0 && (
                                <span style={{
                                    position: 'absolute',
                                    top: '-4px',
                                    right: '-8px',
                                    background: '#ef4444',
                                    color: 'white',
                                    borderRadius: '999px',
                                    fontSize: '10px',
                                    fontWeight: 700,
                                    minWidth: '16px',
                                    height: '16px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: '0 3px'
                                }}>
                                    {unseenFeedbackCount}
                                </span>
                            )}
                        </span>
                        <span>Feedback</span>
                    </div>
                    <div
                        className={`nav-item ${activeTab === 'branding' ? 'active' : ''}`}
                        onClick={() => { setActiveTab('branding'); fetchBranding(); setError(null); setResult(null); }}
                    >
                        <span className="nav-icon"><Icon name="palette" /></span>
                        <span>Branding</span>
                    </div>

                    {/* Logout - visible in mobile nav */}
                    <div className="nav-item logout-item" onClick={handleLogout}>
                        <span className="nav-icon"><Icon name="logout" /></span>
                        <span>Logout</span>
                    </div>
                </nav>
            </div>

            {/* Main Content */}
            <div className="main-content">
                {/* Top Bar */}
                <div className="top-bar">
                    <h2 className="page-title">{getPageTitle()}</h2>
                    <div className="user-info">
                        <span>{reseller.username}</span>
                        <span>•</span>
                        <span>Group: {reseller.userGroup}</span>
                        <span>•</span>
                        <span className="license-badge">
                            {stats.usedLicenses}/{stats.maxLicenses} Licenses
                        </span>
                        <ThemeToggle compact />
                    </div>
                </div>

                {/* Content */}
                <div className="content-wrapper">
                    {/* Alerts */}
                    {error && (
                        <div className="alert alert-error">
                            <span style={{ fontSize: '18px' }}><Icon name="alert" size={16} /></span>
                            <div>
                                <strong>Error</strong>
                                <p style={{ marginTop: '4px', marginBottom: 0 }}>{error}</p>
                            </div>
                        </div>
                    )}
                    {result && (
                        <div className="alert alert-success">
                            <span style={{ display: 'inline-flex' }}><Icon name="check" size={17} /></span>
                            <div><strong>{result}</strong></div>
                        </div>
                    )}

                    {/* Migration Progress Banner */}
                    {migrationStatus && (
                        <div className="alert" style={{
                            background: '#dff4fd',
                            color: '#16232d',
                            borderLeft: '4px solid #0b7da6',
                            padding: '16px',
                            marginBottom: '16px'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                                <span style={{ fontSize: '24px', animation: 'spin 2s linear infinite' }}><Icon name="refresh" size={16} /></span>
                                <div>
                                    <strong style={{ fontSize: '16px' }}>{migrationStatus.message}</strong>
                                    <p style={{ margin: '4px 0 0 0', opacity: 0.9 }}>{migrationStatus.details}</p>
                                </div>
                            </div>
                            <div style={{
                                background: 'rgba(255,255,255,0.2)',
                                borderRadius: '10px',
                                height: '12px',
                                overflow: 'hidden',
                                marginBottom: '8px'
                            }}>
                                <div style={{
                                    width: `${migrationStatus.progress}%`,
                                    height: '100%',
                                    background: '#1b8a5a',
                                    borderRadius: '10px',
                                    transition: 'width 0.5s ease'
                                }}></div>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', opacity: 0.9 }}>
                                <span>{migrationStatus.migratedCount}/{migrationStatus.totalUsers} users migrated ({migrationStatus.progress}%)</span>
                                <span>~{migrationStatus.estimatedMinutesLeft} min remaining</span>
                            </div>
                        </div>
                    )}

                    {/* Dashboard Tab */}
                    {activeTab === 'dashboard' && (
                        <>
                            <div className="stats-grid">
                                <div className="stat-card">
                                    <div className="stat-label">Used Licenses</div>
                                    <div className="stat-value">{stats.usedLicenses}</div>
                                </div>
                                <div className="stat-card" style={{ borderLeftColor: '#10b981' }}>
                                    <div className="stat-label">Available Licenses</div>
                                    <div className="stat-value">{stats.availableLicenses}</div>
                                </div>
                                <div className="stat-card" style={{ borderLeftColor: '#0b7da6' }}>
                                    <div className="stat-label">Total Allocated</div>
                                    <div className="stat-value">{stats.maxLicenses}</div>
                                </div>
                                <div className="stat-card" style={{
                                    borderLeftColor: stats.workingDays > 0 ? ((() => {
                                        if (!stats.createdAt || !stats.workingDays) return '#f59e0b';
                                        const createdDate = new Date(stats.createdAt);
                                        const expiryDate = new Date(createdDate);
                                        expiryDate.setDate(expiryDate.getDate() + stats.workingDays);
                                        const daysRemaining = Math.ceil((expiryDate - new Date()) / (1000 * 60 * 60 * 24));
                                        return daysRemaining <= 7 ? '#ef4444' : daysRemaining <= 30 ? '#f59e0b' : '#10b981';
                                    })()) : '#94a3b8'
                                }}>
                                    <div className="stat-label">Account Validity</div>
                                    <div className="stat-value">
                                        {stats.workingDays > 0 && stats.createdAt ? (() => {
                                            const createdDate = new Date(stats.createdAt);
                                            const expiryDate = new Date(createdDate);
                                            expiryDate.setDate(expiryDate.getDate() + stats.workingDays);
                                            const daysRemaining = Math.ceil((expiryDate - new Date()) / (1000 * 60 * 60 * 24));
                                            return daysRemaining > 0 ? `${daysRemaining} days` : 'Expired';
                                        })() : `${stats.workingDays} days`}
                                    </div>
                                </div>
                            </div>

                            {/* Account Validity Warning */}
                            {stats.workingDays > 0 && stats.createdAt && (() => {
                                const createdDate = new Date(stats.createdAt);
                                const expiryDate = new Date(createdDate);
                                expiryDate.setDate(expiryDate.getDate() + stats.workingDays);
                                const daysRemaining = Math.ceil((expiryDate - new Date()) / (1000 * 60 * 60 * 24));

                                if (daysRemaining <= 0) {
                                    return (
                                        <div style={{ marginBottom: '20px', padding: '16px', background: '#fef2f2', borderRadius: '8px', borderLeft: '4px solid #dc2626' }}>
                                            <strong style={{ color: '#991b1b', fontSize: '15px' }}>Account Expired</strong>
                                            <p style={{ color: '#991b1b', fontSize: '13px', marginTop: '8px', marginBottom: 0 }}>
                                                Your account validity has expired. Please contact your administrator to renew.
                                            </p>
                                        </div>
                                    );
                                } else if (daysRemaining <= 7) {
                                    return (
                                        <div style={{ marginBottom: '20px', padding: '16px', background: '#fef2f2', borderRadius: '8px', borderLeft: '4px solid #f59e0b' }}>
                                            <strong style={{ color: '#92400e', fontSize: '15px' }}>Account Expiring Soon!</strong>
                                            <p style={{ color: '#92400e', fontSize: '13px', marginTop: '8px', marginBottom: 0 }}>
                                                Your account will expire in <strong>{daysRemaining} day(s)</strong>. Contact your administrator to renew.
                                            </p>
                                        </div>
                                    );
                                } else if (daysRemaining <= 30) {
                                    return (
                                        <div style={{ marginBottom: '20px', padding: '16px', background: '#fffbeb', borderRadius: '8px', borderLeft: '4px solid #f59e0b' }}>
                                            <strong style={{ color: '#92400e', fontSize: '15px' }}>Account Validity Notice</strong>
                                            <p style={{ color: '#92400e', fontSize: '13px', marginTop: '8px', marginBottom: 0 }}>
                                                Your account is valid for <strong>{daysRemaining} more days</strong> (expires on {expiryDate.toLocaleDateString()}).
                                            </p>
                                        </div>
                                    );
                                }
                                return null;
                            })()}

                            <div className="card">
                                <div className="card-header">
                                    <h3 className="card-title">License Usage</h3>
                                </div>
                                <div className="license-progress-container">
                                    <div className="license-progress-bar">
                                        <div
                                            className={`license-progress-fill ${licensePercentage >= 90 ? 'danger' : licensePercentage >= 70 ? 'warning' : ''}`}
                                            style={{ width: `${licensePercentage}%` }}
                                        ></div>
                                    </div>
                                    <div className="license-progress-text">
                                        {stats.usedLicenses} of {stats.maxLicenses} licenses used ({licensePercentage}%)
                                    </div>
                                </div>

                                {stats.availableLicenses === 0 && (
                                    <div style={{ marginTop: '20px', padding: '16px', background: '#fef2f2', borderRadius: '6px', borderLeft: '4px solid #ef4444' }}>
                                        <strong style={{ color: '#991b1b', fontSize: '14px' }}>License Limit Reached</strong>
                                        <p style={{ color: '#991b1b', fontSize: '13px', marginTop: '8px', marginBottom: 0 }}>
                                            You have used all your allocated licenses. Contact your administrator to request more.
                                        </p>
                                    </div>
                                )}
                            </div>

                            <div className="card">
                                <div className="card-header">
                                    <h3 className="card-title">Quick Actions</h3>
                                </div>
                                <div className="quick-actions">
                                    <button
                                        className="btn btn-primary"
                                        onClick={() => setActiveTab('assign')}
                                        disabled={stats.availableLicenses === 0}
                                    >
                                        Add New User
                                    </button>
                                    <button
                                        className="btn btn-secondary"
                                        onClick={() => { setActiveTab('users'); fetchUsers(); }}
                                    >
                                        View My Users
                                    </button>
                                </div>
                            </div>
                        </>
                    )}

                    {/* Assign User Tab */}
                    {activeTab === 'assign' && (
                        <div className="card">
                            <div className="card-header">
                                <h3 className="card-title">Add User to {reseller.userGroup}</h3>
                            </div>

                            <div style={{ marginBottom: '20px', padding: '16px', background: '#dbeafe', borderRadius: '6px', borderLeft: '4px solid #3b82f6' }}>
                                <strong style={{ color: '#1e40af', fontSize: '14px' }}>License Info</strong>
                                <p style={{ color: '#1e40af', fontSize: '13px', marginTop: '8px', marginBottom: 0 }}>
                                    You have <strong>{stats.availableLicenses}</strong> licenses available out of <strong>{stats.maxLicenses}</strong> total.
                                </p>
                            </div>

                            {stats.availableLicenses === 0 ? (
                                <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
                                    <div style={{ fontSize: '48px', marginBottom: '16px' }}><Icon name="x" size={16} /></div>
                                    <h4 style={{ marginBottom: '8px', color: '#334155' }}>No Licenses Available</h4>
                                    <p>Contact your administrator to request additional licenses.</p>
                                </div>
                            ) : (
                                <form onSubmit={handleAssignUser}>
                                    <div className="form-group">
                                        <label className="form-label">User Email Address</label>
                                        <input
                                            type="email"
                                            className="form-input"
                                            placeholder="user@example.com"
                                            value={email}
                                            onChange={e => setEmail(e.target.value)}
                                            required
                                        />
                                    </div>

                                    {/* Organization selector - resellers choose which org to assign to */}
                                    {organizationAccess.length > 1 && (
                                        <div className="form-group">
                                            <label className="form-label">Select Organization</label>
                                            <select
                                                className="form-input"
                                                value={selectedOrg?.organizationId || ''}
                                                onChange={e => {
                                                    const orgId = parseInt(e.target.value);
                                                    const org = organizationAccess.find(a => String(a.organizationId) === String(orgId));
                                                    setSelectedOrg(org);
                                                    setSelectedProducts(getAssignableProducts(org));
                                                }}
                                            >
                                                {organizationAccess.map((org, i) => (
                                                    <option key={i} value={org.organizationId}>
                                                        {org.organizationName}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    )}

                                    {organizationAccess.length === 1 && selectedOrg && (
                                        <div className="form-group">
                                            <label className="form-label">Organization</label>
                                            <div style={{ padding: '10px', background: '#f0f9ff', borderRadius: '6px', fontSize: '14px', color: '#1e40af' }}><Icon name="building" size={16} /><strong>{selectedOrg.organizationName}</strong>
                                            </div>
                                        </div>
                                    )}

                                    {/* Product selection - filtered by selected org */}
                                    {selectedOrg && selectedOrg.products && selectedOrg.products.length > 0 && (
                                        <div className="form-group">
                                            <label className="form-label">Select Products to Assign</label>
                                            <div style={{
                                                border: '1px solid #e2e8f0',
                                                borderRadius: '6px',
                                                padding: '12px',
                                                background: '#fff'
                                            }}>
                                                {assignableProducts(selectedOrg.products).map((p, i) => {
                                                    const productFull = isProductFull(selectedOrg, p);
                                                    return (
                                                    <label key={i} style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        padding: '8px',
                                                        cursor: productFull ? 'not-allowed' : 'pointer',
                                                        borderRadius: '4px',
                                                        opacity: productFull ? 0.55 : 1,
                                                        background: selectedProducts.includes(p) ? '#dbeafe' : 'transparent'
                                                    }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedProducts.includes(p)}
                                                            disabled={productFull}
                                                            onChange={e => {
                                                                if (e.target.checked) {
                                                                    setSelectedProducts([...selectedProducts, p]);
                                                                } else {
                                                                    setSelectedProducts(selectedProducts.filter(prod => prod !== p));
                                                                }
                                                            }}
                                                            style={{ marginRight: '10px' }}
                                                        />
                                                        <span style={{ fontSize: '13px' }}>{p}{formatProductAvailability(selectedOrg, p)}</span>
                                                    </label>
                                                    );
                                                })}
                                            </div>
                                            {selectedProducts.length === 0 && (
                                                <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>
                                                    Please select at least one product
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Duration Selection */}
                                    <div className="form-group">
                                        <label className="form-label">License Duration</label>
                                        <select
                                            className="form-input"
                                            value={durationMonths}
                                            onChange={e => setDurationMonths(parseInt(e.target.value))}
                                        >
                                            {availableActivationMonths.map(m => (
                                                <option key={m} value={m}>{m} {m === 1 ? 'Month' : 'Months'}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="btn btn-primary"
                                        style={{ width: '100%' }}
                                    >
                                        {loading ? 'Processing...' : 'Add User'}
                                    </button>
                                </form>
                            )}
                        </div>
                    )}

                    {/* Bulk Import Tab */}
                    {activeTab === 'bulk' && (
                        <div className="card">
                            <div className="card-header">
                                <h3 className="card-title">Bulk Import Users</h3>
                                {bulkUserLimit > 0 && (
                                    <span style={{ fontSize: '13px', color: '#f97316', fontWeight: 600, background: '#fff7ed', border: '1px solid #fdba74', borderRadius: '6px', padding: '4px 10px' }}>
                                        Maximum Users per Bulk Operation: {bulkUserLimit}
                                    </span>
                                )}
                            </div>

                            {/* License Info Banner */}
                            <div style={{
                                background: '#dff4fd',
                                padding: '16px 20px',
                                borderRadius: '8px',
                                marginBottom: '20px',
                                border: '1px solid #bfdbfe'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <span style={{ fontSize: '20px' }}><Icon name="info" size={16} /></span>
                                    <div>
                                        <strong style={{ color: '#1e40af' }}>License Info</strong>
                                        <div style={{ color: '#3b82f6', fontSize: '14px' }}>
                                            You have <strong>{stats.availableLicenses}</strong> licenses available out of <strong>{stats.maxLicenses}</strong> total.
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <form onSubmit={handleBulkImport}>
                                {/* Organization Selection */}
                                <div className="form-group">
                                    <label className="form-label">Organization</label>
                                    {organizationAccess.length === 1 ? (
                                        <div style={{
                                            padding: '12px',
                                            background: '#f0f9ff',
                                            borderRadius: '6px',
                                            border: '1px solid #bae6fd',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px'
                                        }}>
                                            <span><Icon name="building" size={16} /></span>
                                            <strong>{organizationAccess[0]?.organizationName}</strong>
                                        </div>
                                    ) : (
                                        <select
                                            className="form-input"
                                            value={selectedOrg?.organizationId || ''}
                                            onChange={(e) => {
                                                const org = organizationAccess.find(o => String(o.organizationId) === String(e.target.value));
                                                setSelectedOrg(org || null);
                                                setSelectedProducts(getAssignableProducts(org));
                                            }}
                                            required
                                        >
                                            <option value="">-- Select Organization --</option>
                                            {organizationAccess.map((org, i) => (
                                                <option key={i} value={org.organizationId}>
                                                    {org.organizationName}
                                                </option>
                                            ))}
                                        </select>
                                    )}
                                </div>

                                {/* Product Selection */}
                                {selectedOrg && selectedOrg.products && selectedOrg.products.length > 0 && (
                                    <div className="form-group">
                                        <label className="form-label">Select Products to Assign</label>
                                        <div style={{ background: '#f8f9fa', padding: '12px', borderRadius: '6px' }}>
                                            {assignableProducts(selectedOrg.products).map((p, i) => {
                                                const productFull = isProductFull(selectedOrg, p);
                                                return (
                                                <label key={i} style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    marginBottom: '8px',
                                                    cursor: productFull ? 'not-allowed' : 'pointer',
                                                    padding: '8px',
                                                    borderRadius: '4px',
                                                    opacity: productFull ? 0.55 : 1,
                                                    background: selectedProducts.includes(p) ? '#dbeafe' : 'transparent'
                                                }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedProducts.includes(p)}
                                                        disabled={productFull}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                setSelectedProducts([...selectedProducts, p]);
                                                            } else {
                                                                setSelectedProducts(selectedProducts.filter(prod => prod !== p));
                                                            }
                                                        }}
                                                        style={{ marginRight: '10px', width: '18px', height: '18px' }}
                                                    />
                                                    <span style={{ fontSize: '13px' }}>{p}{formatProductAvailability(selectedOrg, p)}</span>
                                                </label>
                                                );
                                            })}
                                        </div>
                                        {selectedProducts.length === 0 && (
                                            <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>
                                                Please select at least one product
                                            </div>
                                        )}
                                        {selectedProducts.length > 0 && (
                                            <div style={{ color: '#059669', fontSize: '12px', marginTop: '4px' }}>
                                                {selectedProducts.length} product(s) selected
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Duration Selection */}
                                <div className="form-group">
                                    <label className="form-label">License Duration</label>
                                    <select
                                        className="form-input"
                                        value={bulkDurationMonths}
                                        onChange={e => setBulkDurationMonths(parseInt(e.target.value))}
                                    >
                                        {availableActivationMonths.map(m => (
                                            <option key={m} value={m}>{m} {m === 1 ? 'Month' : 'Months'}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* Email List Input */}
                                <div className="form-group">
                                    <label className="form-label">Email Addresses (comma or newline separated) *</label>
                                    <textarea
                                        className="form-input"
                                        placeholder="user1@example.com, user2@example.com&#10;user3@example.com&#10;user4@example.com"
                                        value={bulkCsvContent}
                                        onChange={e => setBulkCsvContent(e.target.value)}
                                        rows={8}
                                        style={{ fontFamily: 'monospace', resize: 'vertical' }}
                                        required
                                    />
                                    <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                                        Paste a list of email addresses. Each email will be assigned the selected products.
                                    </div>
                                </div>

                                <button
                                    type="submit"
                                    disabled={loading || !selectedOrg || selectedProducts.length === 0}
                                    className="btn btn-primary"
                                    style={{ width: '100%' }}
                                >
                                    {loading && bulkProgress.total > 0 ? (() => {
                                        const { current, total, startTime } = bulkProgress;
                                        if (current === 0) {
                                            return `Starting... 0/${total}`;
                                        }
                                        const elapsed = (Date.now() - startTime) / 1000;
                                        const perUser = elapsed / current;
                                        const remaining = Math.ceil((total - current) * perUser);
                                        const mins = Math.floor(remaining / 60);
                                        const secs = remaining % 60;
                                        const eta = remaining > 60 ? `~${mins}m ${secs}s left` : `~${remaining}s left`;
                                        const pct = Math.round((current / total) * 100);
                                        return `${current}/${total} (${pct}%) - ${eta}`;
                                    })() : loading ? 'Processing...' : 'Import & Assign Users'}
                                </button>
                            </form>
                        </div>
                    )}

                    {/* Bulk Delete Tab */}
                    {activeTab === 'bulkDelete' && stats.allowDeleteUsers === true && (
                        <div className="card">
                            <div className="card-header">
                                <h3 className="card-title">Bulk Delete Users</h3>
                            </div>

                            <div style={{ marginBottom: '20px', padding: '16px', background: '#fef2f2', borderRadius: '6px', borderLeft: '4px solid #ef4444' }}>
                                <strong style={{ color: '#991b1b', fontSize: '14px' }}>Warning: Permanent Deletion</strong>
                                <p style={{ color: '#991b1b', fontSize: '13px', marginTop: '8px', marginBottom: 0 }}>
                                    Deleting users will remove them from your organization and free up their licenses. This action cannot be undone.
                                </p>
                            </div>

                            <form onSubmit={handleBulkDelete}>
                                <div className="form-group">
                                    <label className="form-label">Email Addresses (comma, newline, or semicolon separated) *</label>
                                    <textarea
                                        className="form-input"
                                        placeholder="user1@example.com, user2@example.com&#10;user3@example.com&#10;user4@example.com"
                                        value={bulkDeleteEmails}
                                        onChange={e => setBulkDeleteEmails(e.target.value)}
                                        rows={10}
                                        style={{ fontFamily: 'monospace', resize: 'vertical' }}
                                        required
                                    />
                                    <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                                        Paste a list of email addresses to delete. Only users you have assigned will be deleted.
                                    </div>
                                </div>

                                {bulkDeleteEmails.trim() && (
                                    <div style={{ marginBottom: '16px', padding: '12px', background: '#f8fafc', borderRadius: '6px' }}>
                                        <strong style={{ fontSize: '13px' }}>Preview:</strong>
                                        <div style={{ fontSize: '12px', color: '#374151', marginTop: '4px' }}>
                                            {bulkDeleteEmails
                                                .split(/[\n,;]+/)
                                                .map(e => e.trim().toLowerCase())
                                                .filter(e => e && e.includes('@'))
                                                .length} email(s) detected
                                        </div>
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    disabled={loading || !bulkDeleteEmails.trim()}
                                    className="btn btn-danger"
                                    style={{ width: '100%' }}
                                >
                                    {loading ? 'Deleting...' : 'Delete All Listed Users'}
                                </button>
                            </form>
                        </div>
                    )}

                    {/* Bulk Extend Tab */}
                    {activeTab === 'bulkExtend' && (
                        <div className="card">
                            <div className="card-header">
                                <h3 className="card-title">Bulk Extend Users</h3>
                            </div>

                            <div style={{ marginBottom: '20px', padding: '16px', background: '#ecfdf5', borderRadius: '6px', borderLeft: '4px solid #10b981' }}>
                                <strong style={{ color: '#065f46', fontSize: '14px' }}>Extend Expiration Dates</strong>
                                <p style={{ color: '#065f46', fontSize: '13px', marginTop: '8px', marginBottom: 0 }}>
                                    Add months to multiple users' expiration dates. Extension is added to remaining time.
                                </p>
                            </div>

                            <form onSubmit={handleBulkExtend}>
                                <div className="form-group">
                                    <label className="form-label">Extension Duration</label>
                                    <select
                                        className="form-input"
                                        value={bulkExtendMonths}
                                        onChange={e => setBulkExtendMonths(parseInt(e.target.value))}
                                    >
                                        {availableActivationMonths.map(m => (
                                            <option key={m} value={m}>{m} {m === 1 ? 'Month' : 'Months'}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label className="form-label">Email Addresses (comma, newline, or semicolon separated) *</label>
                                    <textarea
                                        className="form-input"
                                        placeholder="user1@example.com, user2@example.com&#10;user3@example.com"
                                        value={bulkExtendEmails}
                                        onChange={e => setBulkExtendEmails(e.target.value)}
                                        rows={10}
                                        style={{ fontFamily: 'monospace', resize: 'vertical' }}
                                        required
                                    />
                                    <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                                        Paste a list of email addresses to extend. Only users you have assigned will be extended.
                                    </div>
                                </div>

                                {bulkExtendEmails.trim() && (
                                    <div style={{ marginBottom: '16px', padding: '12px', background: '#f8fafc', borderRadius: '6px' }}>
                                        <strong style={{ fontSize: '13px' }}>Preview:</strong>
                                        <div style={{ fontSize: '12px', color: '#374151', marginTop: '4px' }}>
                                            {bulkExtendEmails
                                                .split(/[\n,;]+/)
                                                .map(e => e.trim().toLowerCase())
                                                .filter(e => e && e.includes('@'))
                                                .length} email(s) will be extended by {bulkExtendMonths} month(s)
                                        </div>
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    disabled={loading || !bulkExtendEmails.trim()}
                                    className="btn btn-primary"
                                    style={{ width: '100%' }}
                                >
                                    {loading ? 'Extending...' : 'Extend All Listed Users'}
                                </button>
                            </form>
                        </div>
                    )}

                    {/* History Tab — shows auto-removed users and all activity */}
                    {activeTab === 'history' && (
                        <div className="card">
                            <div className="card-header">
                                <h3 className="card-title">User History & Auto-Removal Log</h3>
                                <button onClick={fetchActivity} disabled={loadingActivity} className="btn btn-secondary">
                                    Refresh
                                </button>
                            </div>

                            <div style={{ padding: '12px', background: '#fffbeb', borderRadius: '8px', marginBottom: '16px', border: '1px solid #fde68a' }}>
                                <strong style={{ color: '#92400e' }}>About Auto-Removal</strong>
                                <p style={{ fontSize: '13px', color: '#92400e', marginTop: '6px', marginBottom: 0 }}>
                                    Users are automatically removed from Adobe 24 hours after their subscription expires.
                                    This log shows all user activity including auto-removals so you can see exactly when and why users disappeared.
                                </p>
                            </div>

                            {loadingActivity ? (
                                <div className="loading">Loading history...</div>
                            ) : activityLogs.length === 0 ? (
                                <div className="empty-state">
                                    <div className="empty-icon"><Icon name="clipboard" size={16} /></div>
                                    <div className="empty-text">No activity recorded yet</div>
                                </div>
                            ) : (
                                <table className="data-table">
                                    <thead>
                                        <tr>
                                            <th>Date</th>
                                            <th>Action</th>
                                            <th>Email / Details</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {activityLogs.map((log, idx) => {
                                            const isAutoRemoved = log.action === 'USER_EXPIRED_REMOVED';
                                            const isRemoved = log.action === 'USER_REMOVED' || isAutoRemoved;
                                            const details = log.details || {};
                                            return (
                                                <tr key={idx} style={{ background: isAutoRemoved ? '#fef2f2' : 'inherit' }}>
                                                    <td style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>
                                                        {new Date(log.timestamp).toLocaleString()}
                                                    </td>
                                                    <td>
                                                        <span style={{
                                                            padding: '2px 8px',
                                                            borderRadius: '12px',
                                                            fontSize: '11px',
                                                            fontWeight: 700,
                                                            background: isAutoRemoved ? '#fee2e2' : isRemoved ? '#fef3c7' : log.action === 'USER_ASSIGNED' ? '#dcfce7' : '#f1f5f9',
                                                            color: isAutoRemoved ? '#991b1b' : isRemoved ? '#92400e' : log.action === 'USER_ASSIGNED' ? '#166534' : '#475569'
                                                        }}>
                                                            {isAutoRemoved ? 'Auto-Expired' : log.action.replace(/_/g, ' ')}
                                                        </span>
                                                    </td>
                                                    <td style={{ fontSize: '12px' }}>
                                                        <div><strong>{details.email || details.oldEmail || '—'}</strong></div>
                                                        {details.expiresAt && (
                                                            <div style={{ color: '#dc2626', fontSize: '11px' }}>
                                                                Expired: {new Date(details.expiresAt).toLocaleDateString()}
                                                            </div>
                                                        )}
                                                        {details.reason && (
                                                            <div style={{ color: '#64748b', fontSize: '11px' }}>{details.reason}</div>
                                                        )}
                                                        {details.organizationName && (
                                                            <div style={{ color: '#64748b', fontSize: '11px' }}>Org: {details.organizationName}</div>
                                                        )}
                                                        {details.products && Array.isArray(details.products) && (
                                                            <div style={{ color: '#64748b', fontSize: '11px' }}>Products: {details.products.join(', ')}</div>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    )}

                    {activeTab === 'feedback' && (
                        <div className="card">
                            <div className="card-header">
                                <h3 className="card-title">Feedback</h3>
                                <button onClick={fetchMyFeedback} disabled={loadingFeedback} className="btn btn-secondary">
                                    Refresh
                                </button>
                            </div>

                            <div style={{ padding: '16px', background: '#f8fafc', borderRadius: '8px', marginBottom: '24px', border: '1px solid #e2e8f0' }}>
                                <h4 style={{ marginTop: 0, marginBottom: '16px' }}>Report a bug or suggest an improvement</h4>
                                <form onSubmit={handleSubmitFeedback}>
                                    <div className="form-group">
                                        <label className="form-label">Type</label>
                                        <select
                                            className="form-input"
                                            value={feedbackType}
                                            onChange={e => setFeedbackType(e.target.value)}
                                        >
                                            <option value="suggestion">Suggestion</option>
                                            <option value="bug">Bug Report</option>
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Message</label>
                                        <textarea
                                            className="form-input"
                                            value={feedbackMessage}
                                            onChange={e => setFeedbackMessage(e.target.value)}
                                            placeholder="Describe the bug or your suggestion in detail..."
                                            rows={4}
                                            maxLength={2000}
                                            required
                                        />
                                    </div>
                                    <div style={{ textAlign: 'right', marginTop: '16px' }}>
                                        <button type="submit" disabled={submittingFeedback || !feedbackMessage.trim()} className="btn btn-primary">
                                            {submittingFeedback ? 'Submitting...' : 'Submit Feedback'}
                                        </button>
                                    </div>
                                </form>
                            </div>

                            <h4 style={{ marginBottom: '12px' }}>Your Past Feedback ({myFeedback.length})</h4>
                            {loadingFeedback ? (
                                <div className="loading">Loading feedback...</div>
                            ) : myFeedback.length === 0 ? (
                                <div className="empty-state">
                                    <div className="empty-text">You haven't submitted any feedback yet</div>
                                </div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                    {myFeedback.map((fb) => {
                                        const statusColors = {
                                            new: { bg: '#fef3c7', color: '#92400e' },
                                            reviewed: { bg: '#dbeafe', color: '#1e40af' },
                                            resolved: { bg: '#dcfce7', color: '#166534' },
                                            dismissed: { bg: '#f1f5f9', color: '#64748b' }
                                        };
                                        const sc = statusColors[fb.status] || statusColors.new;
                                        const isUnseenReply = !!(fb.adminNote && !fb.resellerSeenAt);
                                        return (
                                            <div key={fb.id} style={{
                                                padding: '14px',
                                                background: isUnseenReply ? '#fffbeb' : 'white',
                                                borderRadius: '8px',
                                                border: isUnseenReply ? '1px solid #fbbf24' : '1px solid #e2e8f0',
                                                borderLeft: `4px solid ${fb.type === 'bug' ? '#ef4444' : '#3b82f6'}`,
                                                boxShadow: isUnseenReply ? '0 0 0 1px #fbbf24' : 'none'
                                            }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px', flexWrap: 'wrap' }}>
                                                    <span className="badge" style={{ background: fb.type === 'bug' ? '#fee2e2' : '#dbeafe', color: fb.type === 'bug' ? '#991b1b' : '#1e40af', fontSize: '10px' }}>
                                                        {fb.type === 'bug' ? 'Bug' : 'Suggestion'}
                                                    </span>
                                                    <span className="badge" style={{ background: sc.bg, color: sc.color, fontSize: '10px', textTransform: 'capitalize' }}>
                                                        {fb.status}
                                                    </span>
                                                    {isUnseenReply && (
                                                        <span className="badge" style={{ background: '#f59e0b', color: 'white', fontSize: '10px', fontWeight: 700 }}>
                                                            New reply
                                                        </span>
                                                    )}
                                                    <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                                                        {new Date(fb.createdAt).toLocaleString()}
                                                    </span>
                                                </div>
                                                <div style={{ whiteSpace: 'pre-wrap', color: '#475569', fontSize: '14px' }}>
                                                    {fb.message}
                                                </div>
                                                {fb.adminNote && (
                                                    <div style={{ marginTop: '8px', padding: '8px 10px', background: '#f0f9ff', borderRadius: '6px', fontSize: '13px', color: '#0369a1' }}>
                                                        <strong>Admin response:</strong> {fb.adminNote}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'branding' && (
                        <div className="card">
                            <div className="card-header">
                                <h3 className="card-title">Status Page Branding</h3>
                                <button onClick={fetchBranding} disabled={loadingBranding} className="btn btn-secondary">
                                    Refresh
                                </button>
                            </div>

                            <p style={{ color: '#64748b', fontSize: '13px', marginTop: 0, marginBottom: '20px' }}>
                                Customize what your customers see on the public subscription status page. Changes apply
                                once a customer looks up an email that belongs to your organization.
                            </p>

                            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(280px, 1fr) 320px', gap: '24px', alignItems: 'start' }}>
                                <form onSubmit={handleSaveBranding}>
                                    <div className="form-group">
                                        <label className="form-label">Header Title</label>
                                        <input
                                            className="form-input"
                                            type="text"
                                            value={brandingForm.title}
                                            onChange={e => setBrandingForm({ ...brandingForm, title: e.target.value })}
                                            placeholder="Check Your Subscription"
                                            maxLength={80}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Header Subtitle</label>
                                        <input
                                            className="form-input"
                                            type="text"
                                            value={brandingForm.subtitle}
                                            onChange={e => setBrandingForm({ ...brandingForm, subtitle: e.target.value })}
                                            placeholder="Enter your email to view your Adobe subscription status"
                                            maxLength={160}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Brand Color (buttons)</label>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                            <input
                                                type="color"
                                                value={brandingForm.brandColor || '#ffa239'}
                                                onChange={e => setBrandingForm({ ...brandingForm, brandColor: e.target.value })}
                                                style={{ width: '44px', height: '38px', padding: '2px', border: '1px solid #e2e8f0', borderRadius: '8px', cursor: 'pointer' }}
                                            />
                                            <input
                                                className="form-input"
                                                type="text"
                                                value={brandingForm.brandColor}
                                                onChange={e => setBrandingForm({ ...brandingForm, brandColor: e.target.value })}
                                                placeholder="#ffa239 (default)"
                                                style={{ flex: 1 }}
                                            />
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label">Header Logo</label>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                            <div style={{ width: '56px', height: '56px', borderRadius: '12px', background: '#f1f5f9', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
                                                {branding.logoUrl ? <img src={`${API_URL}${branding.logoUrl}`} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ fontSize: '24px' }}><Icon name="clipboard" size={16} /></span>}
                                            </div>
                                            <label className="btn btn-secondary" style={{ cursor: 'pointer' }}>
                                                {uploadingLogo ? 'Uploading...' : 'Upload'}
                                                <input type="file" accept="image/png,image/jpeg,image/webp" style={{ display: 'none' }} disabled={uploadingLogo}
                                                    onChange={e => { if (e.target.files[0]) handleBrandingImageUpload('logo', e.target.files[0]); e.target.value = ''; }} />
                                            </label>
                                            {branding.logoUrl && (
                                                <button type="button" className="btn btn-secondary" onClick={() => handleRemoveBrandingImage('logo')}>Remove</button>
                                            )}
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label">Organization Icon</label>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                            <div style={{ width: '56px', height: '56px', borderRadius: '12px', background: '#f1f5f9', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
                                                {branding.orgIconUrl ? <img src={`${API_URL}${branding.orgIconUrl}`} alt="Org icon" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ fontSize: '24px' }}><Icon name="building" size={16} /></span>}
                                            </div>
                                            <label className="btn btn-secondary" style={{ cursor: 'pointer' }}>
                                                {uploadingOrgIcon ? 'Uploading...' : 'Upload'}
                                                <input type="file" accept="image/png,image/jpeg,image/webp" style={{ display: 'none' }} disabled={uploadingOrgIcon}
                                                    onChange={e => { if (e.target.files[0]) handleBrandingImageUpload('orgIcon', e.target.files[0]); e.target.value = ''; }} />
                                            </label>
                                            {branding.orgIconUrl && (
                                                <button type="button" className="btn btn-secondary" onClick={() => handleRemoveBrandingImage('orgIcon')}>Remove</button>
                                            )}
                                        </div>
                                    </div>

                                    <div style={{ textAlign: 'right', marginTop: '16px' }}>
                                        <button type="submit" disabled={savingBranding} className="btn btn-primary">
                                            {savingBranding ? 'Saving...' : 'Save Branding'}
                                        </button>
                                    </div>
                                </form>

                                <div>
                                    <div className="form-label" style={{ marginBottom: '8px' }}>Preview</div>
                                    <div style={{ borderRadius: '12px', padding: '24px 16px', background: '#eef8fd', border: '1px solid #d9e6ed', textAlign: 'center' }}>
                                        <div style={{ width: '56px', height: '56px', background: '#ffffff', border: '1px solid #d9e6ed', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px', overflow: 'hidden' }}>
                                            {branding.logoUrl ? <img src={`${API_URL}${branding.logoUrl}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ fontSize: '28px' }}><Icon name="clipboard" size={16} /></span>}
                                        </div>
                                        <div style={{ color: '#16232d', fontWeight: 700, fontSize: '18px', marginBottom: '6px' }}>{brandingForm.title || 'Check Your Subscription'}</div>
                                        <div style={{ color: '#5d6d79', fontSize: '12px', marginBottom: '16px' }}>{brandingForm.subtitle || 'Enter your email to view your Adobe subscription status'}</div>
                                        <div style={{ padding: '10px', borderRadius: '10px', background: brandingForm.brandColor || '#ffa239', color: brandingForm.brandColor ? 'white' : '#3d2200', fontSize: '13px', fontWeight: 600 }}>
                                            Check Status
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Users Tab */}
                    {activeTab === 'users' && (
                        <>
                            <div className="stats-grid">
                                <div className="stat-card">
                                    <div className="stat-label">Total Users</div>
                                    <div className="stat-value">{users.length}</div>
                                </div>
                                <div className="stat-card" style={{ borderLeftColor: '#10b981' }}>
                                    <div className="stat-label">Filtered Results</div>
                                    <div className="stat-value">{filteredUsers.length}</div>
                                </div>
                            </div>

                            <div className="card">
                                <div className="card-header">
                                    <h3 className="card-title">{reseller.userGroup} Team Members</h3>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <button
                                            onClick={handleExportUsers}
                                            disabled={users.length === 0}
                                            className="btn btn-secondary"
                                            title="Export all users to CSV"
                                        >
                                            Export
                                        </button>
                                        <button
                                            onClick={fetchUsers}
                                            disabled={loadingUsers}
                                            className="btn btn-secondary"
                                        >
                                            Refresh
                                        </button>
                                    </div>
                                </div>

                                {/* Bulk Action Toolbar - Always show when users exist */}
                                {filteredUsers.length > 0 && (
                                    <div style={{
                                        padding: '12px 16px',
                                        background: selectedUsers.length > 0 ? '#dbeafe' : '#f8fafc',
                                        borderRadius: '8px',
                                        marginBottom: '16px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        flexWrap: 'wrap',
                                        gap: '12px'
                                    }}>
                                        <span style={{ fontSize: '14px', color: '#374151' }}>
                                            {selectedUsers.length > 0
                                                ? `${selectedUsers.length} user(s) selected`
                                                : 'Select users for bulk actions'}
                                        </span>
                                        {selectedUsers.length > 0 && (
                                            <>
                                                {/* Bulk Remove Button */}
                                                {stats.allowDeleteUsers === true && (
                                                <button
                                                    onClick={async () => {
                                                        if (!window.confirm(`Remove ${selectedUsers.length} user(s)?`)) return;
                                                        setBulkChangeLoading(true);
                                                        let success = 0, fail = 0;
                                                        for (const email of selectedUsers) {
                                                            try {
                                                                const res = await fetch(`${API_URL}/reseller/remove-user`, {
                                                                    method: 'POST',
                                                                    headers: authHeaders,
                                                                    body: JSON.stringify({ email })
                                                                });
                                                                if ((await res.json()).success) success++; else fail++;
                                                            } catch { fail++; }
                                                        }
                                                        setBulkChangeLoading(false);
                                                        setSelectedUsers([]);
                                                        fetchUsers();
                                                        fetchStats();
                                                        setResult(`Removed ${success} user(s)${fail > 0 ? `, ${fail} failed` : ''}`);
                                                    }}
                                                    disabled={bulkChangeLoading}
                                                    className="btn btn-danger"
                                                    style={{ padding: '8px 16px' }}
                                                >
                                                    {bulkChangeLoading ? 'Removing…' : 'Remove Selected'}
                                                </button>
                                                )}

                                                {/* Modify Duration Button */}
                                                <button
                                                    onClick={() => setShowBulkDurationModal(true)}
                                                    className="btn btn-primary"
                                                    style={{ padding: '8px 16px', background: '#f59e0b', borderColor: '#f59e0b' }}
                                                >
                                                    Modify Duration
                                                </button>

                                                {/* Bulk Transfer Button */}
                                                {stats.allowTransfer !== false && (
                                                <button
                                                    onClick={() => setShowBulkTransferModal(true)}
                                                    className="btn btn-primary"
                                                    style={{ padding: '8px 16px', background: '#0b7da6', borderColor: '#0b7da6' }}
                                                >
                                                    Bulk Transfer
                                                </button>
                                                )}


                                                {/* Org Change - only show if 2+ orgs and transfer allowed */}
                                                {
                                                    organizationAccess.length > 1 && stats.allowTransfer !== false && (
                                                        <>
                                                            <select
                                                                value={targetOrgForBulkChange?.organizationId || ''}
                                                                onChange={(e) => {
                                                                    const org = organizationAccess.find(a => a.organizationId === parseInt(e.target.value));
                                                                    setTargetOrgForBulkChange(org || null);
                                                                }}
                                                                style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #d1d5db' }}
                                                            >
                                                                <option value="">Move to org...</option>
                                                                {organizationAccess.map(org => (
                                                                    <option key={org.organizationId} value={org.organizationId}>
                                                                        {org.organizationName}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                            <button
                                                                onClick={handleBulkOrgChange}
                                                                disabled={!targetOrgForBulkChange || bulkChangeLoading}
                                                                className="btn btn-primary"
                                                                style={{ padding: '8px 16px' }}
                                                            >
                                                                {bulkChangeLoading ? 'Changing…' : 'Change Org'}
                                                            </button>
                                                        </>
                                                    )
                                                }

                                                <button
                                                    onClick={() => setSelectedUsers([])}
                                                    className="btn btn-secondary"
                                                    style={{ padding: '8px 12px' }}
                                                >
                                                    Clear
                                                </button>
                                            </>
                                        )}
                                    </div>
                                )}

                                <div style={{ marginBottom: '16px' }}>
                                    <div className="search-wrapper" style={{ marginBottom: '12px', display: 'flex', gap: '12px' }}>
                                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '0 12px' }}>
                                            <span className="search-icon"><Icon name="search" size={16} /></span>
                                            <input
                                                type="text"
                                                className="form-input search-input"
                                                placeholder="Search users by email or name..."
                                                value={searchQuery}
                                                onChange={e => setSearchQuery(e.target.value)}
                                                style={{ border: 'none', boxShadow: 'none', padding: '12px 0' }}
                                            />
                                        </div>
                                        <select
                                            className="form-input"
                                            value={selectedOrgFilter}
                                            onChange={(e) => setSelectedOrgFilter(e.target.value)}
                                            style={{ width: '200px' }}
                                        >
                                            <option value="All">All Organizations</option>
                                            <option value="Unknown">Unknown / No Org</option>
                                            {allUniqueOrgs.map(org => (
                                                <option key={org.organizationId} value={org.organizationId}>
                                                    {org.organizationName}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Advanced Filters */}
                                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                                        {/* Duration Filter */}
                                        <div style={{ minWidth: '140px' }}>
                                            <label style={{ fontSize: '11px', color: '#666', display: 'block', marginBottom: '4px' }}>Duration</label>
                                            <select
                                                value={durationFilter}
                                                onChange={(e) => setDurationFilter(e.target.value)}
                                                className="form-input"
                                                style={{ fontSize: '13px', padding: '8px' }}
                                            >
                                                <option value="">All Durations</option>
                                                <option value="1">1 Month</option>
                                                <option value="3">3 Months</option>
                                                <option value="6">6 Months</option>
                                                <option value="12">12 Months</option>
                                            </select>
                                        </div>

                                        {/* Days Remaining Filter */}
                                        <div style={{ minWidth: '160px' }}>
                                            <label style={{ fontSize: '11px', color: '#666', display: 'block', marginBottom: '4px' }}>Days Remaining ≤</label>
                                            <input
                                                type="number"
                                                value={daysFilter}
                                                onChange={(e) => setDaysFilter(e.target.value)}
                                                placeholder="e.g., 7"
                                                className="form-input"
                                                style={{ fontSize: '13px', padding: '8px' }}
                                                min="0"
                                            />
                                        </div>

                                        {/* Clear Filters */}
                                        {(durationFilter || daysFilter) && (
                                            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                                                <button
                                                    onClick={() => {
                                                        setDurationFilter('');
                                                        setDaysFilter('');
                                                    }}
                                                    className="btn btn-secondary"
                                                    style={{ padding: '8px 12px', fontSize: '12px' }}
                                                >
                                                    Clear Filters
                                                </button>
                                            </div>
                                        )}
                                    </div>

                                    {(searchQuery || durationFilter || daysFilter) && (
                                        <div style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
                                            Showing {filteredUsers.length} of {users.length} users
                                            {daysFilter && ` (expiring in ≤ ${daysFilter} days)`}
                                        </div>
                                    )}
                                </div>

                                {loadingUsers && <div className="loading">Loading users...</div>}

                                {
                                    !loadingUsers && filteredUsers.length > 0 && (
                                        <>
                                            <table className="data-table">
                                                <thead>
                                                    <tr>
                                                        <th style={{ width: '40px', textAlign: 'center' }}>
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                                                                onChange={toggleSelectAll}
                                                                style={{ width: '18px', height: '18px' }}
                                                            />
                                                        </th>
                                                        <th>Email Address</th>
                                                        <th>Organization</th>
                                                        <th>Products</th>
                                                        <th>Duration</th>
                                                        <th>Expires</th>
                                                        <th style={{ textAlign: 'center' }}>Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {paginatedUsers.map((user, index) => {
                                                        // Calculate expiration status
                                                        const expiresAt = user.expiresAt ? new Date(user.expiresAt) : null;
                                                        const assignedAt = user.assignedAt ? new Date(user.assignedAt) : null;
                                                        const now = new Date();

                                                        // Calculate total duration, days used, days left
                                                        const totalDays = user.durationMonths ? user.durationMonths * 30 : 30;
                                                        const daysLeft = expiresAt ? Math.ceil((expiresAt - now) / (1000 * 60 * 60 * 24)) : null;
                                                        const daysUsed = assignedAt ? Math.floor((now - assignedAt) / (1000 * 60 * 60 * 24)) : 0;
                                                        const progressPercent = totalDays > 0 ? Math.min(100, Math.max(0, ((totalDays - (daysLeft || 0)) / totalDays) * 100)) : 0;

                                                        let statusBadge = '●';
                                                        let statusColor = '#10b981';
                                                        let barColor = '#10b981';

                                                        if (daysLeft !== null) {
                                                            if (daysLeft <= 0) {
                                                                statusBadge = '●';
                                                                statusColor = '#ef4444';
                                                                barColor = '#ef4444';
                                                            } else if (daysLeft <= 7) {
                                                                statusBadge = '●';
                                                                statusColor = '#f59e0b';
                                                                barColor = '#f59e0b';
                                                            }
                                                        }

                                                        return (
                                                            <tr key={index} style={{ background: selectedUsers.includes(user.email) ? '#f0f9ff' : 'inherit' }}>
                                                                <td style={{ textAlign: 'center' }}>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={selectedUsers.includes(user.email)}
                                                                        onChange={() => toggleUserSelection(user.email)}
                                                                        style={{ width: '18px', height: '18px' }}
                                                                    />
                                                                </td>
                                                                <td>
                                                                    <div>{user.email}</div>
                                                                    <div style={{ fontSize: '11px', color: '#999' }}>{user.firstname} {user.lastname}</div>
                                                                </td>
                                                                <td>
                                                                    <div style={{ fontSize: '12px', color: '#333' }}>
                                                                        {user.organizationName || organizationAccess.find(o => o.organizationId === user.organizationId)?.organizationName || 'Unknown'}
                                                                    </div>
                                                                </td>
                                                                <td>
                                                                    <div style={{ fontSize: '12px', color: '#666' }}>
                                                                        {user.products && user.products.length > 0
                                                                            ? user.products.join(', ')
                                                                            : <em style={{ color: '#999' }}>No products</em>}
                                                                    </div>
                                                                </td>
                                                                <td>
                                                                    <span style={{ fontSize: '13px' }}>
                                                                        {user.durationMonths ? `${user.durationMonths} mo` : '1 mo'}
                                                                    </span>
                                                                </td>
                                                                <td style={{ minWidth: '180px' }}>
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                        <span style={{ color: statusColor }}>{statusBadge}</span>
                                                                        <div style={{ flex: 1 }}>
                                                                            {/* Progress Bar */}
                                                                            <div style={{
                                                                                width: '100%',
                                                                                height: '8px',
                                                                                background: '#e5e7eb',
                                                                                borderRadius: '4px',
                                                                                overflow: 'hidden',
                                                                                marginBottom: '4px'
                                                                            }}>
                                                                                <div style={{
                                                                                    width: `${progressPercent}% `,
                                                                                    height: '100%',
                                                                                    background: barColor,
                                                                                    borderRadius: '4px',
                                                                                    transition: 'width 0.3s ease'
                                                                                }}></div>
                                                                            </div>
                                                                            {/* Days Info */}
                                                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px' }}>
                                                                                <span style={{ color: '#666' }}>{daysUsed}d used</span>
                                                                                <span style={{ color: statusColor, fontWeight: 600 }}>
                                                                                    {daysLeft !== null ? (daysLeft <= 0 ? 'Expired' : `${daysLeft}d left`) : 'Active'}
                                                                                </span>
                                                                            </div>
                                                                            {/* Expiry Date */}
                                                                            {expiresAt && (
                                                                                <div style={{ fontSize: '9px', color: '#999', marginTop: '2px' }}>
                                                                                    Expires: {expiresAt.toLocaleDateString()}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                                <td style={{ textAlign: 'center' }}>
                                                                    <button
                                                                        onClick={() => handleEditClick(user)}
                                                                        className="btn btn-primary"
                                                                        style={{ padding: '4px 8px', fontSize: '12px', marginRight: '8px' }}
                                                                    >
                                                                        Edit
                                                                    </button>
                                                                        <button
                                                                            onClick={() => handleCopyStatusLink(user.email)}
                                                                            className="btn btn-secondary"
                                                                            style={{ padding: '4px 8px', fontSize: '12px', marginRight: '8px' }}
                                                                        >
                                                                            Copy Link
                                                                        </button>
                                                                    {stats.allowDeleteUsers === true && (
                                                                    <button
                                                                        onClick={() => handleRemoveUser(user.email)}
                                                                        className="btn btn-danger"
                                                                        style={{ padding: '4px 8px', fontSize: '12px' }}
                                                                    ><Icon name="trash" size={16} /></button>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>

                                            {/* Pagination Controls */}
                                            {totalPages > 1 && (
                                                <div style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    marginTop: '16px',
                                                    padding: '12px 16px',
                                                    background: '#f8fafc',
                                                    borderRadius: '8px'
                                                }}>
                                                    <span style={{ fontSize: '14px', color: '#64748b' }}>
                                                        Showing {((currentPage - 1) * USERS_PER_PAGE) + 1} - {Math.min(currentPage * USERS_PER_PAGE, filteredUsers.length)} of {filteredUsers.length} users
                                                    </span>
                                                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                                        <button
                                                            onClick={() => setCurrentPage(1)}
                                                            disabled={currentPage === 1}
                                                            className="btn btn-secondary"
                                                            style={{ padding: '6px 10px', fontSize: '12px' }}
                                                        >
                                                            First
                                                        </button>
                                                        <button
                                                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                                            disabled={currentPage === 1}
                                                            className="btn btn-secondary"
                                                            style={{ padding: '6px 10px', fontSize: '12px' }}
                                                        >
                                                            ‹ Prev
                                                        </button>
                                                        <span style={{ padding: '0 12px', fontWeight: '600' }}>
                                                            Page {currentPage} of {totalPages}
                                                        </span>
                                                        <button
                                                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                                            disabled={currentPage === totalPages}
                                                            className="btn btn-secondary"
                                                            style={{ padding: '6px 10px', fontSize: '12px' }}
                                                        >
                                                            Next ›
                                                        </button>
                                                        <button
                                                            onClick={() => setCurrentPage(totalPages)}
                                                            disabled={currentPage === totalPages}
                                                            className="btn btn-secondary"
                                                            style={{ padding: '6px 10px', fontSize: '12px' }}
                                                        >
                                                            Last </button>
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    )
                                }

                                {
                                    !loadingUsers && filteredUsers.length === 0 && users.length > 0 && (
                                        <div className="empty-state">
                                            <div className="empty-icon"><Icon name="search" size={16} /></div>
                                            <div className="empty-text">No users found matching "{searchQuery}"</div>
                                        </div>
                                    )
                                }

                                {
                                    !loadingUsers && users.length === 0 && (
                                        <div className="empty-state">
                                            <div className="empty-icon"><Icon name="users" size={16} /></div>
                                            <div className="empty-text">No users in your team yet</div>
                                            <button
                                                className="btn btn-primary"
                                                onClick={() => setActiveTab('assign')}
                                                style={{ marginTop: '16px' }}
                                            >
                                                Add Your First User
                                            </button>
                                        </div>
                                    )
                                }
                            </div >
                        </>
                    )}
                </div >
            </div >

            {/* Edit User Modal */}
            {
                editingUser && (
                    <div className="modal-overlay">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h3>Edit User: {editingUser.email}</h3>
                                <button className="close-btn" onClick={() => setEditingUser(null)}>×</button>
                            </div>
                            <div className="modal-body">
                                {/* Organization Info - Read Only/Context */}
                                <div style={{ marginBottom: '16px', padding: '10px', background: '#f0f9ff', borderRadius: '4px' }}>
                                    <small style={{ color: '#666' }}>Organization ID: {editingUser.organizationId}</small>
                                    <div style={{ fontWeight: 500, color: '#0369a1' }}>
                                        {organizationAccess.find(o => o.organizationId === editingUser.organizationId)?.organizationName || 'Current Organization'}
                                    </div>
                                </div>

                                <form onSubmit={handleUpdateUser}>
                                    <div className="form-group">
                                        <label className="form-label">Email Address</label>
                                        <input
                                            type="email"
                                            className="form-input"
                                            value={editEmail}
                                            onChange={(e) => setEditEmail(e.target.value)}
                                            required
                                            style={{ width: '100%', padding: '8px', marginBottom: '16px' }}
                                        />
                                        {editEmail !== editingUser.email && (
                                            <div style={{ fontSize: '12px', color: '#ea580c', marginBottom: '16px' }}>
                                                Changing email will transfer the license to this new address.
                                            </div>
                                        )}
                                    </div>

                                    {/* Destination organization — offered only on a replacement,
                                        and only when the admin allows this account to transfer.
                                        A plain product edit still cannot move the license. */}
                                    {editEmail !== editingUser.email
                                        && stats.allowTransfer !== false
                                        && organizationAccess.length > 1 && (() => {
                                            const currentOrgId = String(editingUser.organizationId ?? '');
                                            const selectedOrgId = String(editTargetOrg || currentOrgId);
                                            const orgName = (id) => organizationAccess
                                                .find(o => String(o.organizationId) === String(id))?.organizationName
                                                || editingUser.organizationName
                                                || `Organization ${id}`;

                                            return (
                                                <div className="form-group">
                                                    <label className="form-label">Destination Organization</label>
                                                    <select
                                                        className="form-input"
                                                        value={selectedOrgId}
                                                        onChange={e => {
                                                            const nextOrgId = e.target.value;
                                                            setEditTargetOrg(nextOrgId);
                                                            // Products are per-org: keep only the ones the
                                                            // destination actually offers, matching by name.
                                                            const nextProducts = organizationAccess
                                                                .find(o => String(o.organizationId) === String(nextOrgId))?.products || [];
                                                            setEditFormProducts(prev => prev
                                                                .map(p => nextProducts.find(np => np.toLowerCase() === String(p).toLowerCase()))
                                                                .filter(Boolean));
                                                        }}
                                                        style={{ width: '100%', padding: '8px' }}
                                                    >
                                                        <option value={currentOrgId}>{orgName(currentOrgId)} (current)</option>
                                                        {organizationAccess
                                                            .filter(o => String(o.organizationId) !== currentOrgId)
                                                            .map(o => (
                                                                <option key={o.organizationId} value={String(o.organizationId)}>
                                                                    {o.organizationName}
                                                                </option>
                                                            ))}
                                                    </select>
                                                    {selectedOrgId !== currentOrgId && (
                                                        <div style={{ fontSize: '12px', color: '#ea580c', margin: '8px 0 16px' }}>
                                                            {editingUser.email} will be removed from {orgName(currentOrgId)} and {editEmail} created in {orgName(selectedOrgId)}. Select the products to assign there below.
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })()}

                                    <div className="form-group">
                                        <label className="form-label">Assigned Products</label>
                                        <div style={{
                                            border: '1px solid #e2e8f0',
                                            borderRadius: '6px',
                                            padding: '12px',
                                            background: '#fff',
                                            maxHeight: '200px',
                                            overflowY: 'auto'
                                        }}>
                                            {/* Products available in the destination organization
                                                (the user's own org unless the license is moving) */}
                                            {organizationAccess
                                                .find(o => String(o.organizationId) === String(editTargetOrg || editingUser.organizationId))
                                                ?.products.filter(p => !isBlockedProduct(p)).map((p, i) => (
                                                    <label key={i} style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        padding: '8px',
                                                        cursor: 'pointer',
                                                        borderRadius: '4px',
                                                        background: editFormProducts.includes(p) ? '#dbeafe' : 'transparent',
                                                        borderBottom: '1px solid #f1f5f9'
                                                    }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={editFormProducts.includes(p)}
                                                            onChange={e => {
                                                                if (e.target.checked) {
                                                                    setEditFormProducts([...editFormProducts, p]);
                                                                } else {
                                                                    setEditFormProducts(editFormProducts.filter(prod => prod !== p));
                                                                }
                                                            }}
                                                            style={{ marginRight: '10px' }}
                                                        />
                                                        <span style={{ fontSize: '13px' }}>{p}</span>
                                                    </label>
                                                ))}
                                        </div>
                                        {editFormProducts.length === 0 && (
                                            <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>
                                                Warning: removing all products will leave user with access to nothing.
                                            </div>
                                        )}
                                    </div>

                                    {/* Extend Validity Section */}
                                    <div className="form-group" style={{ marginTop: '16px' }}>
                                        <label className="form-label">Extend Validity Period</label>
                                        <div style={{
                                            padding: '12px',
                                            background: editExtendMonths > 0 ? '#ecfdf5' : '#f8fafc',
                                            borderRadius: '6px',
                                            border: editExtendMonths > 0 ? '1px solid #10b981' : '1px solid #e2e8f0'
                                        }}>
                                            {/* Show current expiry info */}
                                            {editingUser.assignedAt && (
                                                <div style={{ marginBottom: '12px', fontSize: '12px', color: '#666' }}>
                                                    <div>Current Duration: <strong>{editingUser.durationMonths || 1} months</strong></div>
                                                    <div>Assigned: {new Date(editingUser.assignedAt).toLocaleDateString()}</div>
                                                    {(() => {
                                                        const expiresAt = new Date(editingUser.assignedAt);
                                                        expiresAt.setMonth(expiresAt.getMonth() + (editingUser.durationMonths || 1));
                                                        const isExpired = expiresAt < new Date();
                                                        return (
                                                            <div style={{ color: isExpired ? '#dc2626' : '#059669' }}>
                                                                {isExpired ? 'Expired: ' : 'Expires: '}
                                                                {expiresAt.toLocaleDateString()}
                                                            </div>
                                                        );
                                                    })()}
                                                </div>
                                            )}
                                            <select
                                                className="form-input"
                                                value={editExtendMonths}
                                                onChange={e => setEditExtendMonths(parseInt(e.target.value))}
                                                style={{ width: '100%' }}
                                            >
                                                <option value={0}>-- No Extension --</option>
                                                {availableActivationMonths.map(m => (
                                                    <option key={m} value={m}>Extend by {m} {m === 1 ? 'Month' : 'Months'}</option>
                                                ))}
                                            </select>
                                            {editExtendMonths > 0 && (
                                                <div style={{ marginTop: '8px', fontSize: '12px', color: '#059669' }}>
                                                    Will add {editExtendMonths} month(s) to the current expiry date
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="modal-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
                                        <button
                                            type="button"
                                            className="btn btn-secondary"
                                            onClick={() => setEditingUser(null)}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="btn btn-primary"
                                            disabled={loading}
                                        >
                                            {loading ? 'Saving...' : 'Save Changes'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Bulk Duration Modification Modal */}
            {
                showBulkDurationModal && (
                    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
                        <div style={{ background: '#fff', borderRadius: '12px', padding: '24px', maxWidth: '450px', width: '90%' }}>
                            <h3 style={{ marginBottom: '16px' }}>Modify Duration</h3>
                            <p style={{ fontSize: '14px', color: '#666', marginBottom: '16px' }}>
                                Change subscription duration for <strong>{selectedUsers.length} user(s)</strong>
                                <br /><small style={{ color: '#888' }}>Only users assigned more than 24 hours ago will be modified</small>
                            </p>

                            <div className="form-group" style={{ marginBottom: '16px' }}>
                                <label className="form-label">New Duration (months)</label>
                                <select className="form-input" value={bulkNewDuration}
                                    onChange={(e) => setBulkNewDuration(parseInt(e.target.value))}>
                                    {availableActivationMonths.map(m => (
                                        <option key={m} value={m}>{m} month{m > 1 ? 's' : ''}</option>
                                    ))}
                                </select>
                            </div>

                            <div style={{ display: 'flex', gap: '8px' }}>
                                <button onClick={handleBulkModifyDuration} disabled={loading}
                                    className="btn btn-primary" style={{ flex: 1 }}>
                                    {loading ? 'Modifying...' : 'Apply Changes'}
                                </button>
                                <button onClick={() => { setShowBulkDurationModal(false); setBulkNewDuration(1); }}
                                    className="btn btn-secondary">
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Bulk Transfer Modal */}
            {
                showBulkTransferModal && (
                    <div className="modal-overlay" style={{
                        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                        background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
                    }}>
                        <div className="modal-content" style={{
                            background: 'white', padding: '24px', borderRadius: '12px',
                            width: '90%', maxWidth: '500px', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
                        }}>
                            <h3 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '16px', color: '#1e293b' }}>
                                Bulk Transfer Users
                            </h3>

                            <div style={{ padding: '12px', background: '#fff7ed', borderLeft: '4px solid #f97316', marginBottom: '20px', borderRadius: '4px' }}>
                                <strong style={{ color: '#9a3412', display: 'block', marginBottom: '4px' }}>Important Warning</strong>
                                <p style={{ margin: 0, fontSize: '13px', color: '#9a3412' }}>
                                    {keepSourceOrgOnTransfer ? (
                                        <>
                                            Moving <strong>{selectedUsers.length} users</strong> to a new organization will keep them in their current organization and also add them to the new one.
                                            <br />Users will be synced into the new organization without removing Org A.
                                        </>
                                    ) : (
                                        <>
                                            Moving <strong>{selectedUsers.length} users</strong> to a new organization will remove them from their current organization.
                                            <br />Users will be re-created in the new organization.
                                        </>
                                    )}
                                </p>
                            </div>

                            <div className="form-group">
                                <label className="form-label">Target Organization</label>
                                <select
                                    className="form-input"
                                    value={targetOrgForTransfer || ''}
                                    onChange={(e) => {
                                        setTargetOrgForTransfer(e.target.value);
                                        setTargetProductsForTransfer([]);
                                    }}
                                >
                                    <option value="">Select Target Organization...</option>
                                    {organizationAccess.map(org => (
                                        <option key={org.organizationId} value={org.organizationId}>
                                            {org.organizationName}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {targetOrgForTransfer && (
                                <div className="form-group">
                                    <label className="form-label">Select Products (New Org)</label>
                                    <div style={{ maxHeight: '150px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '8px' }}>
                                        {bulkTransferDerivedProducts.length > 0 ? (
                                            bulkTransferDerivedProducts.map(product => (
                                                <label key={product} style={{ display: 'flex', alignItems: 'center', marginBottom: '8px', cursor: 'pointer' }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={targetProductsForTransfer.includes(product)}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                setTargetProductsForTransfer([...targetProductsForTransfer, product]);
                                                            } else {
                                                                setTargetProductsForTransfer(targetProductsForTransfer.filter(p => p !== product));
                                                            }
                                                        }}
                                                        style={{ marginRight: '8px' }}
                                                    />
                                                    <span style={{ fontSize: '14px', color: '#334155' }}>{product}</span>
                                                </label>
                                            ))
                                        ) : (
                                            <div style={{ padding: '8px', color: '#64748b', fontSize: '13px', fontStyle: 'italic' }}>
                                                No allowed products found.
                                            </div>
                                        )}
                                    </div>
                                    <div style={{ marginTop: '4px', fontSize: '12px', color: '#64748b' }}>
                                        {targetProductsForTransfer.length} products selected
                                    </div>
                                </div>
                            )}

                            <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowBulkTransferModal(false);
                                        setTargetOrgForTransfer(null);
                                        setTargetProductsForTransfer([]);
                                    }}
                                    className="btn btn-secondary"
                                    style={{ flex: 1 }}
                                    disabled={loading}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={handleBulkTransfer}
                                    className="btn btn-primary"
                                    style={{ flex: 1 }}
                                    disabled={loading || !targetOrgForTransfer || targetProductsForTransfer.length === 0}
                                >
                                    {loading ? 'Processing...' : 'Start Transfer'}
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }
        </div >
    );
}

export default ResellerDashboard;
